import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Button, Row, Spinner } from "reactstrap";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import { requestJson } from "../../../../helpers/api";
import { InventoryCategory, InventoryFilterOption } from "./InventoryFiltersPanel";
import { ChartCardConfig, ChartData, ChartTabConfig } from "./ChartTabsSection";
import { InventoryStatusTab } from "./InventoryTableTabs";
import { KpiTabConfig, KpiTabItem } from "./KpiTabsSection";
import StockTransferenciasDetailAnalytics from "./StockTransferenciasDetailAnalytics";
import StockTransferenciasDetailTable from "./StockTransferenciasDetailTable";
import StockTransferenciasDetailOffcanvas from "./StockTransferenciasDetailOffcanvas";
import StockTransferenciasFiltersDrawer from "./StockTransferenciasFiltersDrawer";
import { AggregatedViewId, ChartTabId, TableViewId } from "./StockTransferenciasDetailTypes";
import type { TableColumn } from "react-data-table-component";

type StockTransferenciasDetailTabProps = {
  mode?: "transferencias" | "inventarios" | "compras";
};

type ResumenStats = {
  totalMovimientos: number;
  totalCantidad: number;
  destinosActivos: number;
  origenesActivos: number;
  skuUnicos: number;
  promedioPorDestino: number;
  totalCostoSinIva: number;
  totalCostoConIva: number;
};

const MODE_CONFIG = {
  transferencias: {
    endpoint: "/api/informes/stock/movimientos",
    emptyMessage: "No encontramos movimientos con los filtros aplicados.",
  },
  inventarios: {
    endpoint: "/api/informes/stock/inventarios",
    emptyMessage: "No encontramos registros de inventario con los filtros aplicados.",
  },
  compras: {
    endpoint: "/api/informes/stock/compras",
    emptyMessage: "No encontramos compras con los filtros aplicados.",
  },
} as const;

const MODE_COPY = {
  transferencias: {
    movementsLabel: "Movimientos",
    movementsDetail: "Registros en el rango seleccionado",
    unitsLabel: "Unidades movidas",
    unitsDetail: "Suma de cantidades transferidas",
    destinationLabel: "Destinos activos",
    destinationDetail: "Depósitos que reciben mercaderías",
    extraLabel: "Orígenes activos",
    extraDetail: "Depósitos que enviaron mercaderías",
    extraValueAccessor: (stats: ResumenStats) =>
      stats.origenesActivos.toLocaleString("es-PY"),
    valueLabel: "Valor transferido",
    valueDetail: "Cantidad x costo unitario (con IVA)",
    extraIcon: "ri-store-2-line",
    extraColor: "warning",
  },
  inventarios: {
    movementsLabel: "Registros de inventario",
    movementsDetail: "Registros cargados en el rango seleccionado",
    unitsLabel: "Unidades inventariadas",
    unitsDetail: "Suma de cantidades registradas",
    destinationLabel: "Sucursales inventariadas",
    destinationDetail: "Puntos donde se realizó conteo",
    extraLabel: "Artículo únicos",
    extraDetail: "SKUs inventariados en el rango",
    extraValueAccessor: (stats: ResumenStats) =>
      stats.skuUnicos.toLocaleString("es-PY"),
    valueLabel: "Valor inventario",
    valueDetail: "Cantidad x costo unitario (con IVA)",
    extraIcon: "ri-price-tag-3-line",
    extraColor: "secondary",
  },
  compras: {
    movementsLabel: "Registros de compra",
    movementsDetail: "Facturas ingresadas en el rango seleccionado",
    unitsLabel: "Unidades compradas",
    unitsDetail: "Cantidad total recibida",
    destinationLabel: "Depósitos abastecidos",
    destinationDetail: "Depósitos que recibieron mercaderías",
    extraLabel: "Proveedores activos",
    extraDetail: "Proveedores involucrados en las compras",
    extraValueAccessor: (stats: ResumenStats) =>
      stats.origenesActivos.toLocaleString("es-PY"),
    valueLabel: "Valor comprado",
    valueDetail: "Cantidad x costo unitario (con IVA)",
    extraIcon: "ri-store-2-line",
    extraColor: "info",
  },
} as const;

type MovimientoStockApiRow = {
  server: string | null;
  fecha: string | null;
  fecha_carga: string | null;
  tipo_movimiento: string | null;
  envia: string | null;
  recibe: string | null;
  idmovimiento: number | null;
  familia: string | null;
  linea: string | null;
  barcode: string | null;
  idarticulo: string | null;
  descripcion: string | null;
  medida: string | null;
  cantidad: number | null;
  fuente: string;
  costo_unit_sin_iva?: number | null;
  costo_unit_con_iva?: number | null;
  costo_total_sin_iva?: number | null;
  costo_total_con_iva?: number | null;
  periodo_texto?: string | null;
  factura?: string | null;
  nro_factura?: string | null;
  condicion?: string | null;
  iddeposito?: number | null;
  deposito?: string | null;
  idpersona?: number | null;
  persona?: string | null;
  articulo?: string | null;
  presentacion?: string | null;
  usuario?: string | null;
  idmoneda?: number | null;
  impuesto?: number | null;
  exenta?: number | null;
  gravada?: number | null;
  importe_total?: number | null;
  nota?: string | null;
};

type MovimientosAgrupadosApiRow = {
  etiqueta: string;
  cantidad: number;
  valorSinIva: number;
  valorConIva: number;
};

type MovimientosStockResponse = {
  detalle: MovimientoStockApiRow[];
  agrupado: {
    depositos: MovimientosAgrupadosApiRow[];
    familias: MovimientosAgrupadosApiRow[];
    articulos: MovimientosAgrupadosApiRow[];
  };
};

type InventoryRow = {
  server: string;
  fecha: string;
  tipoMovimiento: string;
  envia: string;
  recibe: string;
  movimientoInfo: string;
  familyName: string;
  lineName: string;
  articuloInfo: string;
  articuloLineas: string[];
  articuloEtiqueta: string;
  cantidad: number;
  costUnitSinIva: number | null;
  costUnitConIva: number | null;
  valorSinIva: number | null;
  valorConIva: number | null;
  periodoCosto: string;
};

type AggregatedRow = {
  etiqueta: string;
  cantidad: number;
  valorSinIva: number;
  valorConIva: number;
};

const aggregateInventoryRows = (
  rows: InventoryRow[],
  selector: (row: InventoryRow) => string | null | undefined,
): AggregatedRow[] => {
  const mapa = new Map<string, AggregatedRow>();
  rows.forEach((row) => {
    const raw = selector(row);
    const etiqueta =
      typeof raw === "string"
        ? raw.trim()
        : raw != null
          ? String(raw).trim()
          : "";
    const clave = etiqueta.length > 0 ? etiqueta : "Sin datos";
    const existente = mapa.get(clave) ?? { etiqueta: clave, cantidad: 0, valorSinIva: 0, valorConIva: 0 };
    existente.cantidad += Number(row.cantidad ?? 0);
    existente.valorSinIva += Number(row.valorSinIva ?? 0);
    existente.valorConIva += Number(row.valorConIva ?? 0);
    mapa.set(clave, existente);
  });
  return Array.from(mapa.values()).sort(
    (a, b) =>
      b.valorConIva - a.valorConIva ||
      b.valorSinIva - a.valorSinIva ||
      b.cantidad - a.cantidad ||
      a.etiqueta.localeCompare(b.etiqueta),
  );
};

type MovimientoTotals = {
  totalCantidad: number;
  totalValorSinIva: number;
  totalValorConIva: number;
  promedioCostoSinIva: number | null;
  promedioCostoConIva: number | null;
};

type DetailContext = {
  view: AggregatedViewId;
  label: string;
};

type ChartCardLayout = {
  colProps?: Partial<Record<"xs" | "sm" | "md" | "lg" | "xl" | "xxl", number>>;
  cardClassName?: string;
  bodyClassName?: string;
};

type ChartSectionConfig = {
  idPrefix: string;
  palette: string[];
  distributionType: "polarArea" | "donut";
  distributionTitle: string;
  distributionSubtitle: string;
  distributionHeight?: number;
  rankingTitle: string;
  rankingSubtitle: string;
  radialTitle: string;
  radialSubtitle: string;
  donutTitle: string;
  donutSubtitle: string;
  evolutionTitle: string;
  evolutionSubtitle: string;
  layouts?: ChartCardLayout[];
};

const formatUnidades = (valor: number) =>
  valor.toLocaleString("es-PY", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  });

const formatMoneda = (valor: number) =>
  valor.toLocaleString("es-PY", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  });

const formatCantidad = (valor: number | null | undefined, fractionDigits = 2) =>
  Number(valor ?? 0).toLocaleString("es-PY", {
    minimumFractionDigits: 0,
    maximumFractionDigits: fractionDigits,
  });

const sanitizeFileName = (valor: string) =>
  valor
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/[^a-zA-Z0-9-_]+/g, "_")
    .replace(/_{2,}/g, "_")
    .replace(/^_|_$/g, "")
    .toLowerCase();

const formatCurrencyLabel = (valor: number | null | undefined) =>
  valor == null || Number.isNaN(Number(valor)) ? "—" : `Gs. ${formatMoneda(Number(valor))}`;

const truncateDisplayValue = (texto: string | null | undefined, maxLength = 24) => {
  const safe = ensureText(texto ?? "").trim();
  if (!safe) {
    return { display: "Sin datos", tooltip: undefined };
  }
  if (safe.length <= maxLength) {
    return { display: safe, tooltip: undefined };
  }
  return { display: `${safe.slice(0, maxLength - 1)}…`, tooltip: safe };
};

const calcularTotalesMovimientos = (filas: InventoryRow[]): MovimientoTotals => {
  let totalCantidad = 0;
  let totalValorSinIva = 0;
  let totalValorConIva = 0;
  let accCostUnitSinIva = 0;
  let countCostUnitSinIva = 0;
  let accCostUnitConIva = 0;
  let countCostUnitConIva = 0;

  filas.forEach((row) => {
    totalCantidad += Number(row.cantidad ?? 0);
    totalValorSinIva += Number(row.valorSinIva ?? 0);
    totalValorConIva += Number(row.valorConIva ?? 0);
    if (row.costUnitSinIva != null && Number.isFinite(row.costUnitSinIva)) {
      accCostUnitSinIva += Number(row.costUnitSinIva);
      countCostUnitSinIva += 1;
    }
    if (row.costUnitConIva != null && Number.isFinite(row.costUnitConIva)) {
      accCostUnitConIva += Number(row.costUnitConIva);
      countCostUnitConIva += 1;
    }
  });

  return {
    totalCantidad,
    totalValorSinIva,
    totalValorConIva,
    promedioCostoSinIva: countCostUnitSinIva > 0 ? accCostUnitSinIva / countCostUnitSinIva : null,
    promedioCostoConIva: countCostUnitConIva > 0 ? accCostUnitConIva / countCostUnitConIva : null,
  };
};

const ensureText = (value: unknown) => {
  if (typeof value === "string") return value;
  if (value == null) return "";
  return String(value);
};

const resolveUsuarioActual = () => {
  if (typeof window === "undefined") {
    return "Usuario no identificado";
  }
  const raw =
    sessionStorage.getItem("authUser") ||
    localStorage.getItem("authUser") ||
    sessionStorage.getItem("user") ||
    localStorage.getItem("user");
  if (!raw) return "Usuario no identificado";
  try {
    const parsed = JSON.parse(raw);
    const nestedUser =
      parsed?.usuario ||
      parsed?.user ||
      parsed?.usuarioSesion ||
      parsed?.datosUsuario ||
      parsed?.data?.usuario ||
      parsed?.data?.user;
    const candidates = [
      parsed?.name,
      parsed?.fullName,
      parsed?.username,
      parsed?.email,
      parsed?.userName,
      nestedUser?.nombre,
      nestedUser?.nombreUsuario,
      nestedUser?.username,
      nestedUser?.fullName,
      nestedUser?.email,
    ];
    const firstString = candidates.find((value) => typeof value === "string" && value.trim().length > 0);
    if (firstString) {
      return ensureText(firstString);
    }
    return ensureText(raw);
  } catch {
    return ensureText(raw);
  }
};

const buildChartCards = (data: ChartData, config: ChartSectionConfig): ChartCardConfig[] => {
  const normalizedSeries = data.series.map((valor) => Number(valor ?? 0));
  const normalizedValues = data.values?.map((valor) => Number(valor ?? 0)) ?? data.series.map(() => 0);
  const combinedEntries = data.labels.map((label, index) => ({
    label,
    quantity: normalizedSeries[index] ?? 0,
    value: normalizedValues[index] ?? 0,
  }));
  const totalCantidad = combinedEntries.reduce((acc, item) => acc + item.quantity, 0);
  const totalValor = combinedEntries.reduce((acc, item) => acc + item.value, 0);
  const topFive = combinedEntries.slice(0, 5);
  const mainEntry = combinedEntries[0] ?? { label: "Sin datos", quantity: 0, value: 0 };
  const sharePrincipal = totalCantidad > 0 ? Number(((mainEntry.quantity / totalCantidad) * 100).toFixed(2)) : 0;
  const sharePrincipalValor = totalValor > 0 ? Number(((mainEntry.value / totalValor) * 100).toFixed(2)) : 0;
  const topCincoCantidad = topFive.reduce((acc, item) => acc + item.quantity, 0);
  const topCincoValor = topFive.reduce((acc, item) => acc + item.value, 0);
  const restoCantidad = Math.max(totalCantidad - topCincoCantidad, 0);
  const restoValor = Math.max(totalValor - topCincoValor, 0);
  const rankingSeries = [
    {
      name: "Unidades",
      data: topFive.map((item) => Number(item.quantity.toFixed(2))),
    },
    {
      name: "Valor c/IVA",
      data: topFive.map((item) => Number(item.value.toFixed(2))),
    },
  ];
  const rankingCategories = topFive.map((item) => item.label);
  const lineEntries = combinedEntries.slice(0, 10);
  const lineSeries = [
    {
      name: "Unidades",
      data: lineEntries.map((item) => Number(item.quantity.toFixed(2))),
    },
    {
      name: "Valor c/IVA",
      data: lineEntries.map((item) => Number(item.value.toFixed(2))),
    },
  ];
  const valueByLabel = new Map<string, number>(combinedEntries.map((entry) => [entry.label, entry.value]));
  const formatDualMetric = (cantidad: number, valor: number) =>
    `${formatUnidades(cantidad)} unidades · Gs. ${formatMoneda(valor)}`;

  const charts: ChartCardConfig[] = [
    {
      id: `${config.idPrefix}-distribution`,
      title: config.distributionTitle,
      subtitle: config.distributionSubtitle,
      type: config.distributionType,
      height: config.distributionHeight ?? 280,
      series: normalizedSeries,
      options: {
        chart: { toolbar: { show: false } },
        labels: data.labels,
        dataLabels: { enabled: true },
        legend: { position: "bottom" },
        colors: config.palette,
        tooltip: {
          y: {
            formatter: (valor: number, opts: any) => {
              const label = opts?.w?.globals?.labels?.[opts.seriesIndex] ?? "";
              const monto = valueByLabel.get(label) ?? 0;
              return formatDualMetric(valor, monto);
            },
          },
        },
        ...(config.distributionType === "polarArea"
          ? { stroke: { colors: ["#fff"] }, fill: { opacity: 0.85 } }
          : { stroke: { show: false } }),
      },
      hasData: totalCantidad > 0 && data.labels.length > 0,
    },
    {
      id: `${config.idPrefix}-ranking`,
      title: config.rankingTitle,
      subtitle: config.rankingSubtitle,
      type: "bar",
      height: 300,
      series: rankingSeries,
      options: {
        chart: { toolbar: { show: false } },
        plotOptions: {
          bar: { horizontal: true, borderRadius: 4, dataLabels: { position: "top" } },
        },
        colors: [config.palette[0] ?? "#4b93ff", config.palette[1] ?? "#f97316"],
        legend: {
          position: "bottom",
          markers: { shape: "square" },
          formatter: (seriesName: string, opts: any) => {
            const totalSeriesCount = opts?.w?.globals?.seriesTotals?.[opts.seriesIndex];
            if (opts.seriesIndex === 1 && typeof totalSeriesCount === "number") {
              return `${seriesName} · Gs. ${formatMoneda(totalSeriesCount)}`;
            }
            return seriesName;
          },
        },
        dataLabels: {
          enabled: true,
          formatter: (valor: number, opts: any) =>
            opts?.seriesIndex === 0 ? formatUnidades(valor) : `Gs. ${formatMoneda(valor)}`,
        },
        xaxis: {
          categories: rankingCategories,
          labels: {
            formatter: (value: string) => formatUnidades(Number(value)),
          },
        },
        tooltip: {
          shared: true,
          intersect: false,
          y: [
            { formatter: (valor: number) => `${formatUnidades(valor)} unidades` },
            { formatter: (valor: number) => `Gs. ${formatMoneda(valor)}` },
          ],
        },
      },
      hasData: rankingCategories.length > 0 && topFive.some((item) => item.quantity > 0 || item.value > 0),
    },
    {
      id: `${config.idPrefix}-radial`,
      title: config.radialTitle,
      subtitle: config.radialSubtitle,
      type: "radialBar",
      height: 300,
      series: [sharePrincipal],
      options: {
        chart: { toolbar: { show: false } },
        colors: [config.palette[0] ?? "#4b93ff"],
        labels: [mainEntry.label],
        plotOptions: {
          radialBar: {
            hollow: { size: "60%" },
            dataLabels: {
              name: { fontSize: "14px" },
              value: {
                formatter: () => `${sharePrincipal.toFixed(1)}% uds · ${sharePrincipalValor.toFixed(1)}% valor`,
              },
            },
          },
        },
        tooltip: {
          y: {
            formatter: () => `${sharePrincipal.toFixed(1)}% unidades · ${sharePrincipalValor.toFixed(1)}% valor`,
          },
        },
      },
      hasData: mainEntry.quantity > 0 && (totalCantidad > 0 || totalValor > 0),
    },
    {
      id: `${config.idPrefix}-top-rest`,
      title: config.donutTitle,
      subtitle: config.donutSubtitle,
      type: "donut",
      height: 260,
      series: [Number(topCincoCantidad.toFixed(2)), Number(restoCantidad.toFixed(2))],
      options: {
        chart: { toolbar: { show: false } },
        labels: ["Top 5", "Resto"],
        dataLabels: { enabled: true },
        legend: { position: "bottom" },
        colors: [config.palette[0] ?? "#4b93ff", "#d1d5db"],
        tooltip: {
          y: {
            formatter: (valor: number, opts: any) => {
              const index = opts?.seriesIndex ?? 0;
              const monto = index === 0 ? topCincoValor : restoValor;
              return formatDualMetric(valor, monto);
            },
          },
        },
      },
      hasData: topCincoCantidad > 0 || topCincoValor > 0,
    },
    {
      id: `${config.idPrefix}-trend`,
      title: config.evolutionTitle,
      subtitle: config.evolutionSubtitle,
      type: "line",
      height: 220,
      series: lineSeries,
      options: {
        chart: {
          toolbar: { show: false },
          zoom: { enabled: false },
          sparkline: { enabled: lineEntries.length > 8 },
        },
        stroke: { curve: "smooth", width: [3, 3] },
        colors: [config.palette[1] ?? "#f97316", config.palette[2] ?? "#34d399"],
        dataLabels: { enabled: false },
        xaxis: {
          categories: lineEntries.map((item) => item.label),
          labels: {
            show: lineEntries.length <= 8,
          },
        },
        yaxis: [
          {
            title: { text: "Unidades" },
            labels: {
              formatter: (value: number) => formatUnidades(value),
            },
          },
          {
            opposite: true,
            title: { text: "Gs. con IVA" },
            labels: {
              formatter: (value: number) => `Gs. ${formatMoneda(value)}`,
            },
          },
        ],
        markers: { size: 3 },
        tooltip: {
          shared: true,
          intersect: false,
          y: [
            { formatter: (valor: number) => `${formatUnidades(valor)} unidades` },
            { formatter: (valor: number) => `Gs. ${formatMoneda(valor)}` },
          ],
        },
        legend: {
          position: "bottom",
          markers: { shape: "circle" },
          formatter: (seriesName: string) => seriesName,
        },
      },
      hasData: lineEntries.some((item) => item.quantity > 0 || item.value > 0),
    },
  ];

  return charts.map((chart, index) => {
    const layout = config.layouts?.[index];
    return {
      ...chart,
      colProps: layout?.colProps,
      cardClassName: layout?.cardClassName,
      bodyClassName: layout?.bodyClassName,
    };
  });
};

const construirFechaIso = (offsetDias: number) => {
  const fecha = new Date();
  fecha.setDate(fecha.getDate() + offsetDias);
  return fecha.toISOString().slice(0, 10);
};

const BREADCRUMB_TEXT: Record<Required<StockTransferenciasDetailTabProps>["mode"], string> = {
  transferencias: "Informes / Stock - Transferencias / Detalle",
  inventarios: "Informes / Stock - Inventarios / Detalle",
  compras: "Informes / Stock - Compras / Detalle",
};

const StockTransferenciasDetailTab: React.FC<StockTransferenciasDetailTabProps> = ({
  mode = "transferencias",
}) => {
  const modeSettings = MODE_CONFIG[mode];
  const modeCopy = MODE_COPY[mode];
  const breadcrumbTexto = BREADCRUMB_TEXT[mode];
  const isInventarios = mode === "inventarios";
  const isCompras = mode === "compras";
  const isInventoryLike = isInventarios || isCompras;
  const destinationGroupLabel = isInventarios ? "Sucursales" : isCompras ? "Depósitos" : "Destinos";
  const originGroupLabel = isInventarios ? "Depósitos origen" : isCompras ? "Proveedores" : "Orígenes";
  const skuGroupLabel = isInventoryLike ? "Artículos" : "SKU";
  const destinationSingular = isInventarios ? "Sucursal" : isCompras ? "Depósito" : "Destino";
  const skuSingular = isInventoryLike ? "Artículo" : "SKU";
  const tipoColumnHeader = isInventarios ? "Tipo / Registro" : isCompras ? "Tipo / Factura" : "Tipo / Movimiento";
  const destinoColumnHeader = isInventarios
    ? "Sucursal / Depósito"
    : isCompras
      ? "Proveedor / Depósito"
      : "Origen / Destino";
  const destinoSecondaryLabel = isInventoryLike ? "Depósito" : "Recibe";
  const costoUnitarioHeader = isInventarios
    ? "Costo unitario inventario"
    : isCompras
      ? "Costo unitario compra"
      : "Costo unitario";
  const valorHeader = isInventarios ? "Valor inventario" : isCompras ? "Valor compra" : "Valor transferido";
  const periodoHeader = isInventarios ? "Período costo" : isCompras ? "Factura / Nota" : "Costo origen";
  const destinationSummaryDetail = isInventarios
    ? "Sucursales inventariadas en el rango"
    : isCompras
      ? "Depósitos abastecidos en el rango"
      : "Depósitos que reciben transferencias";
  const destinationAverageLabel = isInventarios
    ? "Promedio por sucursal"
    : isCompras
      ? "Promedio por depósito"
      : "Promedio por destino";
  const destinationAverageDetail = isInventarios
    ? "Unidades registradas por sucursal"
    : isCompras
      ? "Unidades recibidas por depósito"
      : "Unidades promedio recibidas";
  const destinationMainLabel = isInventarios
    ? "Sucursal principal"
    : isCompras
      ? "Depósito principal"
      : "Destino principal";
  const destinationTopDetail = isInventarios
    ? "Peso de las 5 sucursales con mayor inventario"
    : isCompras
      ? "Peso de los 5 depósitos con mayores compras"
      : "Peso de los 5 destinos principales";
  const originSummaryDetail = isInventarios
    ? "Depósitos involucrados en el inventario"
    : isCompras
      ? "Proveedores que abastecieron en el rango"
      : "Depósitos que enviaron mercaderías";
  const originUnitsDetail = isInventarios
    ? "Unidades registradas por depósito"
    : isCompras
      ? "Unidades compradas por proveedor"
      : "Unidades enviadas por origen";
  const originMovDetail = isInventarios
    ? "Registros generados por depósito"
    : isCompras
      ? "Registros generados por proveedor"
      : "Movimientos generados por origen";
  const originMainLabel = isInventarios
    ? "Depósito principal"
    : isCompras
      ? "Proveedor principal"
      : "Origen principal";
  const originTopDetail = isInventarios
    ? "Peso de los 5 depósitos con mayor inventario"
    : isCompras
      ? "Peso de los 5 proveedores con más compras"
      : "Peso de los 5 orígenes principales";
  const skuUniqueLabel = isInventoryLike ? "Artículos únicos" : "SKU únicos";
  const skuLeaderLabel = isInventoryLike ? "Artículo líder" : "SKU líder";
  const skuUniqueDetail = isInventarios
    ? "Artículos diferentes inventariados"
    : isCompras
      ? "Artículos diferentes comprados"
      : "Artículos diferentes transferidos";
  const skuUnitsTotalDetail = isInventarios
    ? "Sumatoria de unidades registradas"
    : isCompras
      ? "Sumatoria de unidades compradas"
      : "Sumatoria de unidades transferidas";
  const skuAverageDetail = isInventoryLike ? "Unidades promedio por artículo" : "Unidades promedio por SKU";
  const destinationDistributionTitle = isInventarios
    ? "Distribución por sucursal"
    : isCompras
      ? "Distribución por depósito"
      : "Distribución por destino";
  const destinationDistributionSubtitle = isInventarios
    ? "Sucursales inventariadas"
    : isCompras
      ? "Depósitos abastecidos"
      : "Depósitos que reciben mercaderías";
  const destinationRankingTitle = isInventarios
    ? "Top 5 sucursales"
    : isCompras
      ? "Top 5 depósitos"
      : "Top 5 destinos";
  const destinationRankingSubtitle = isInventarios
    ? "Stock registrado"
    : isCompras
      ? "Unidades compradas"
      : "Unidades recibidas";
  const destinationRadialTitle = isInventarios
    ? "Sucursal líder"
    : isCompras
      ? "Depósito líder"
      : "Destino líder";
  const destinationDonutSubtitle = isInventarios
    ? "Comparativa según stock por sucursal"
    : isCompras
      ? "Comparativa de unidades por depósito"
      : "Comparativa de unidades recibidas";
  const destinationEvolutionTitle = isInventarios
    ? "Tendencia de sucursales"
    : isCompras
      ? "Tendencia de depósitos"
      : "Tendencia de destinos";
  const destinationEvolutionSubtitle = isInventarios
    ? "Top 10 sucursales por unidades"
    : isCompras
      ? "Top 10 depósitos por compras"
      : "Top 10 destinos por unidades";
  const originDistributionTitle = isInventarios
    ? "Distribución por depósito"
    : isCompras
      ? "Distribución por proveedor"
      : "Distribución por origen";
  const originDistributionSubtitle = isInventarios
    ? "Depósitos analizados"
    : isCompras
      ? "Proveedores con compras"
      : "Depósitos que envían mercaderías";
  const originRankingTitle = isInventarios
    ? "Top 5 depósitos"
    : isCompras
      ? "Top 5 proveedores"
      : "Top 5 orígenes";
  const originRankingSubtitle = isInventarios
    ? "Unidades registradas"
    : isCompras
      ? "Unidades compradas"
      : "Unidades enviadas";
  const originRadialTitle = isInventarios
    ? "Depósito líder"
    : isCompras
      ? "Proveedor líder"
      : "Origen líder";
  const originDonutSubtitle = isInventarios
    ? "Comparativa de depósitos"
    : isCompras
      ? "Comparativa de proveedores"
      : "Comparativa de orígenes";
  const originEvolutionTitle = isInventarios
    ? "Tendencia de depósitos"
    : isCompras
      ? "Tendencia de proveedores"
      : "Tendencia de orígenes";
  const originEvolutionSubtitle = isInventarios
    ? "Top 10 depósitos inventariados"
    : isCompras
      ? "Top 10 proveedores por compras"
      : "Top 10 depósitos emisores";
  const skuDistributionTitle = isInventoryLike ? "Distribución por artículo" : "Distribución por SKU";
  const skuDistributionSubtitle = isInventoryLike
    ? isCompras
      ? "Artículos con mayor participación en compras"
      : "Artículos con mayor existencia"
    : "Artículos con mayor movimiento";
  const skuRankingTitle = isInventoryLike ? "Top 5 artículos" : "Top 5 SKU";
  const skuRankingSubtitle = isInventoryLike
    ? isCompras
      ? "Unidades compradas"
      : "Stock registrado"
    : "Unidades transferidas";
  const skuRadialTitle = skuLeaderLabel;
  const skuTopDetail = isInventoryLike
    ? isCompras
      ? "Aporte de los 5 artículos más comprados"
      : "Aporte de los 5 artículos con mayor inventario"
    : "Aporte de los 5 SKU más movidos";
  const skuComparisonDetail = isInventoryLike ? "Comparativa de artículos" : "Comparativa de SKU";
  const skuEvolutionTitle = isInventoryLike ? "Tendencia de artículos" : "Tendencia de SKU";
  const skuEvolutionSubtitle = isInventoryLike ? "Top 10 artículos por unidades" : "Top 10 SKU por unidades";
  const listadoDescripcionBase = isInventarios
    ? "Registros inventariados"
    : isCompras
      ? "Compras registradas"
      : "Movimientos registrados";
  const listadoTitulo = isInventarios
    ? "Listado de inventario"
    : isCompras
      ? "Listado de compras"
      : "Listado de transferencias";
  const exportSheetName = isInventarios ? "Inventarios" : isCompras ? "Compras" : "Transferencias";
  const exportPrefix = isInventarios ? "inventarios" : isCompras ? "compras" : "transferencias";
  const aggregatedLabelByView = useMemo(
    () => ({
      depositos: destinationSingular,
      familias: "Familia",
      articulos: skuSingular,
      proveedores: "Proveedor",
    }),
    [destinationSingular, skuSingular],
  );
  const [fechaDesde, setFechaDesde] = useState(construirFechaIso(-7));
  const [fechaHasta, setFechaHasta] = useState(construirFechaIso(0));
  const [movimientos, setMovimientos] = useState<MovimientoStockApiRow[]>([]);
  const [selectedTipos, setSelectedTipos] = useState<InventoryFilterOption[]>([]);
  const [selectedIdMovs, setSelectedIdMovs] = useState<InventoryFilterOption[]>([]);
  const [selectedFamilias, setSelectedFamilias] = useState<InventoryFilterOption[]>([]);
  const [selectedLineas, setSelectedLineas] = useState<InventoryFilterOption[]>([]);
  const [selectedRecibes, setSelectedRecibes] = useState<InventoryFilterOption[]>([]);
  const [selectedDescripciones, setSelectedDescripciones] = useState<InventoryFilterOption[]>([]);
  const [selectedServers, setSelectedServers] = useState<InventoryFilterOption[]>([]);
  const [activeCategory, setActiveCategory] = useState("all");
  const [activeTableTab, setActiveTableTab] = useState<TableViewId>("detalle");
  const [loadingMovimientos, setLoadingMovimientos] = useState(false);
  const [errorMovimientos, setErrorMovimientos] = useState<string | null>(null);
  const [hasFetched, setHasFetched] = useState(false);
  const [filtrosAbiertos, setFiltrosAbiertos] = useState(false);
  const [exportando, setExportando] = useState(false);
  const [kpiTab, setKpiTab] = useState("resumen");
  const [chartsTab, setChartsTab] = useState<ChartTabId>("destinos");
  const [detalleContext, setDetalleContext] = useState<DetailContext | null>(null);
  const [exportandoDetalleExcel, setExportandoDetalleExcel] = useState(false);
  const [exportandoDetallePdf, setExportandoDetallePdf] = useState(false);
  const [exportandoListadoPdf, setExportandoListadoPdf] = useState(false);
  const [exportandoTablaActualExcel, setExportandoTablaActualExcel] = useState(false);
  const [exportandoTablaActualPdf, setExportandoTablaActualPdf] = useState(false);
  const [logoDataUrl, setLogoDataUrl] = useState<string | null>(null);

  useEffect(() => {
    let activo = true;
    const cargarLogo = async () => {
      try {
        const respuesta = await fetch("/baconappen_529x124.png");
        if (!respuesta.ok) return;
        const blob = await respuesta.blob();
        const lector = new FileReader();
        lector.onloadend = () => {
          if (activo && typeof lector.result === "string") {
            setLogoDataUrl(lector.result);
          }
        };
        lector.readAsDataURL(blob);
      } catch {
        // ignorado
      }
    };
    void cargarLogo();
    return () => {
      activo = false;
    };
  }, []);

  const tipoOptions = useMemo<InventoryFilterOption[]>(() => {
    const valores = Array.from(
      new Set(
        movimientos
          .map((mov) => (mov.tipo_movimiento ?? "").trim())
          .filter((valor) => valor.length > 0),
      ),
    );
    return valores.map((valor) => ({ value: valor, label: valor }));
  }, [movimientos]);

  const categoryOptions = useMemo<InventoryCategory[]>(() => {
    const contador = new Map<string, number>();
    movimientos.forEach((mov) => {
      const clave = mov.envia?.trim() || "Sin origen";
      contador.set(clave, (contador.get(clave) || 0) + 1);
    });
    return [
      { value: "all", label: "Todos los depósitos", count: movimientos.length },
      ...Array.from(contador.entries()).map(([valor, cantidad]) => ({
        value: valor,
        label: valor,
        count: cantidad,
      })),
    ];
  }, [movimientos]);

  const buildOptionsFromMovimientos = useCallback(
    (getter: (mov: MovimientoStockApiRow) => string) => {
      const valores = new Set<string>();
      movimientos.forEach((mov) => {
        const valor = getter(mov);
        if (valor) {
          valores.add(valor);
        }
      });
      return Array.from(valores)
        .sort((a, b) => a.localeCompare(b))
        .map((valor) => ({ value: valor, label: valor }));
    },
    [movimientos],
  );

  const idMovOptions = useMemo(
    () =>
      buildOptionsFromMovimientos((mov) =>
        mov.idmovimiento != null && Number.isFinite(mov.idmovimiento) ? String(mov.idmovimiento) : "",
      ),
    [buildOptionsFromMovimientos],
  );
  const familiaOptions = useMemo(
    () => buildOptionsFromMovimientos((mov) => (mov.familia ?? "").trim()),
    [buildOptionsFromMovimientos],
  );
  const lineaOptions = useMemo(
    () => buildOptionsFromMovimientos((mov) => (mov.linea ?? "").trim()),
    [buildOptionsFromMovimientos],
  );
  const serverOptions = useMemo(
    () => buildOptionsFromMovimientos((mov) => (mov.server ?? mov.fuente ?? "").trim()),
    [buildOptionsFromMovimientos],
  );
  const recibeOptions = useMemo(
    () => buildOptionsFromMovimientos((mov) => (mov.recibe ?? "").trim()),
    [buildOptionsFromMovimientos],
  );
  const descripcionOptions = useMemo(
    () => buildOptionsFromMovimientos((mov) => (mov.descripcion ?? "").trim()),
    [buildOptionsFromMovimientos],
  );

  const filteredMovimientos = useMemo(() => {
    const tipoPermitido = selectedTipos.length > 0 ? new Set(selectedTipos.map((opt) => opt.value)) : null;
    const idSet = selectedIdMovs.length > 0 ? new Set(selectedIdMovs.map((opt) => opt.value)) : null;
    const familiaSet = selectedFamilias.length > 0 ? new Set(selectedFamilias.map((opt) => opt.value)) : null;
    const lineaSet = selectedLineas.length > 0 ? new Set(selectedLineas.map((opt) => opt.value)) : null;
    const serverSet = selectedServers.length > 0 ? new Set(selectedServers.map((opt) => opt.value)) : null;
    const recibeSet = selectedRecibes.length > 0 ? new Set(selectedRecibes.map((opt) => opt.value)) : null;
    const descripcionSet =
      selectedDescripciones.length > 0 ? new Set(selectedDescripciones.map((opt) => opt.value)) : null;

    return movimientos.filter((mov) => {
      if (activeCategory !== "all") {
        const origen = mov.envia?.trim() || "Sin origen";
        if (origen !== activeCategory) {
          return false;
        }
      }
      if (tipoPermitido && !tipoPermitido.has((mov.tipo_movimiento ?? "").trim())) {
        return false;
      }
      if (idSet) {
        const actual = mov.idmovimiento != null ? String(mov.idmovimiento) : "";
        if (!idSet.has(actual)) return false;
      }
      if (familiaSet) {
        const actual = (mov.familia ?? "").trim();
        if (!familiaSet.has(actual)) return false;
      }
      if (lineaSet) {
        const actual = (mov.linea ?? "").trim();
        if (!lineaSet.has(actual)) return false;
      }
      if (serverSet) {
        const actual = (mov.server ?? mov.fuente ?? "").trim();
        if (!serverSet.has(actual)) return false;
      }
      if (recibeSet) {
        const actual = (mov.recibe ?? "").trim();
        if (!recibeSet.has(actual)) return false;
      }
      if (descripcionSet) {
        const actual = (mov.descripcion ?? "").trim();
        if (!descripcionSet.has(actual)) return false;
      }
      return true;
    });
  }, [
    movimientos,
    activeCategory,
    selectedTipos,
    selectedIdMovs,
    selectedFamilias,
    selectedLineas,
    selectedServers,
    selectedRecibes,
    selectedDescripciones,
  ]);

const normalizedRows = useMemo<InventoryRow[]>(() => {
    const formatFecha = (fecha: string | null, fechaCarga: string | null) => {
      const fuente = fechaCarga || fecha;
      if (!fuente) return "";
      const parsed = new Date(fuente);
      if (Number.isNaN(parsed.getTime())) {
        return fuente;
      }
      const pad = (valor: number) => valor.toString().padStart(2, "0");
      const dia = pad(parsed.getDate());
      const mes = pad(parsed.getMonth() + 1);
      const anho = parsed.getFullYear();
      const horas = pad(parsed.getHours());
      const minutos = pad(parsed.getMinutes());
      const segundos = pad(parsed.getSeconds());
      return `${dia}/${mes}/${anho} ${horas}:${minutos}:${segundos}`;
    };

    return filteredMovimientos.map((mov) => {
      const envia = (mov.envia ?? "").trim();
      const recibe = (mov.recibe ?? "").trim();
      const familia = (mov.familia ?? "").trim();
      const linea = (mov.linea ?? "").trim();
      const descripcionLimpia = (mov.descripcion ?? "").trim();
      const barcode = (mov.barcode ?? "").trim();
      const medida = (mov.medida ?? "").trim();
      const codigoArticulo = (mov.idarticulo ?? "").trim() || "Sin código";
      const descripcionArticulo = descripcionLimpia || "Sin descripción";
      const articuloEtiqueta = `${codigoArticulo} - ${descripcionArticulo}`.trim();
      const articuloPartes = [descripcionLimpia, barcode, medida].filter((valor) => Boolean((valor ?? "").trim()));
      const articuloLineas = articuloPartes.length > 0 ? articuloPartes : ["Sin descripción"];

      return {
        server: mov.server ?? mov.fuente ?? "",
        fecha: formatFecha(mov.fecha, mov.fecha_carga),
        tipoMovimiento: (mov.tipo_movimiento ?? "").trim(),
        envia,
        recibe,
        movimientoInfo: [
          mov.idmovimiento != null && Number.isFinite(mov.idmovimiento) ? `#${mov.idmovimiento}` : null,
          mov.server ?? mov.fuente ?? null,
        ]
          .filter(Boolean)
          .join(" • "),
        familyName: familia,
        lineName: linea,
        articuloInfo: articuloPartes.join(" • "),
        articuloLineas,
        articuloEtiqueta,
        cantidad: Number(mov.cantidad ?? 0),
        costUnitSinIva:
          mov.costo_unit_sin_iva != null && Number.isFinite(Number(mov.costo_unit_sin_iva))
            ? Number(mov.costo_unit_sin_iva)
            : null,
        costUnitConIva:
          mov.costo_unit_con_iva != null && Number.isFinite(Number(mov.costo_unit_con_iva))
            ? Number(mov.costo_unit_con_iva)
            : null,
        valorSinIva:
          mov.costo_total_sin_iva != null && Number.isFinite(Number(mov.costo_total_sin_iva))
            ? Number(mov.costo_total_sin_iva)
            : null,
        valorConIva:
          mov.costo_total_con_iva != null && Number.isFinite(Number(mov.costo_total_con_iva))
            ? Number(mov.costo_total_con_iva)
            : null,
        periodoCosto: mov.periodo_texto ?? "Sin dato",
      };
    });
  }, [filteredMovimientos]);

  const resumenStats = useMemo(() => {
    const totalMovimientos = filteredMovimientos.length;
    let totalCantidad = 0;
    let totalCostoSinIva = 0;
    let totalCostoConIva = 0;
    const destinos = new Set<string>();
    const origenes = new Set<string>();
    const skuSet = new Set<string>();

    filteredMovimientos.forEach((mov) => {
      totalCantidad += Number(mov.cantidad ?? 0);
      totalCostoSinIva += Number(mov.costo_total_sin_iva ?? 0);
      totalCostoConIva += Number(mov.costo_total_con_iva ?? 0);
      const destino = (mov.recibe ?? "").trim();
      if (destino) {
        destinos.add(destino);
      }
      const origen = (mov.envia ?? "").trim();
      if (origen) {
        origenes.add(origen);
      }
      const sku = (mov.idarticulo ?? mov.descripcion ?? "").trim();
      if (sku) {
        skuSet.add(sku);
      }
    });

    const destinosActivos = destinos.size;
    const promedioPorDestino = destinosActivos > 0 ? totalCantidad / destinosActivos : 0;

    return {
      totalMovimientos,
      totalCantidad,
      destinosActivos,
      origenesActivos: origenes.size,
      skuUnicos: skuSet.size,
      promedioPorDestino,
      totalCostoSinIva,
      totalCostoConIva,
    };
  }, [filteredMovimientos]);

  const resumenKpis = useMemo<KpiTabItem[]>(() => {
    const unidadesValor = resumenStats.totalCantidad.toLocaleString("es-PY", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });

    return [
      {
        label: modeCopy.movementsLabel,
        value: resumenStats.totalMovimientos.toLocaleString("es-PY"),
        detalle: modeCopy.movementsDetail,
        icono: "ri-shuffle-line",
        color: "primary",
      },
      {
        label: modeCopy.unitsLabel,
        value: unidadesValor,
        detalle: modeCopy.unitsDetail,
        icono: "ri-box-3-line",
        color: "success",
      },
      {
        label: modeCopy.destinationLabel,
        value: resumenStats.destinosActivos.toLocaleString("es-PY"),
        detalle: modeCopy.destinationDetail,
        icono: "ri-store-3-line",
        color: "info",
      },
      {
        label: modeCopy.extraLabel,
        value: modeCopy.extraValueAccessor(resumenStats),
        detalle: modeCopy.extraDetail,
        icono: modeCopy.extraIcon,
        color: modeCopy.extraColor,
      },
      {
        label: modeCopy.valueLabel,
        value: `Gs. ${formatMoneda(resumenStats.totalCostoConIva)}`,
        detalle: modeCopy.valueDetail,
        icono: "ri-money-dollar-circle-line",
        color: "danger",
      },
    ];
  }, [modeCopy, resumenStats]);

  const graficoPorRecibe = useMemo<ChartData>(() => {
    const totales = new Map<string, { cantidad: number; valor: number }>();
    filteredMovimientos.forEach((mov) => {
      const destino = mov.recibe?.trim() || "Sin destino";
      const cantidad = Number(mov.cantidad ?? 0);
      const valor = Number(mov.costo_total_con_iva ?? 0);
      const entry = totales.get(destino) ?? { cantidad: 0, valor: 0 };
      entry.cantidad += cantidad;
      entry.valor += valor;
      totales.set(destino, entry);
    });
    const ordenados = Array.from(totales.entries()).sort((a, b) => b[1].valor - a[1].valor);
    return {
      labels: ordenados.map(([destino]) => destino),
      series: ordenados.map(([, datos]) => Number(datos.cantidad.toFixed(2))),
      values: ordenados.map(([, datos]) => Number(datos.valor.toFixed(2))),
    };
  }, [filteredMovimientos]);

  const graficoPorTipo = useMemo<ChartData>(() => {
    const totales = new Map<string, { cantidad: number; valor: number }>();
    filteredMovimientos.forEach((mov) => {
      const tipo = mov.tipo_movimiento?.trim() || "Sin tipo";
      const cantidad = Number(mov.cantidad ?? 0);
      const valor = Number(mov.costo_total_con_iva ?? 0);
      const entry = totales.get(tipo) ?? { cantidad: 0, valor: 0 };
      entry.cantidad += cantidad;
      entry.valor += valor;
      totales.set(tipo, entry);
    });
    const ordenados = Array.from(totales.entries()).sort((a, b) => b[1].valor - a[1].valor);
    return {
      labels: ordenados.map(([tipo]) => tipo),
      series: ordenados.map(([, datos]) => Number(datos.cantidad.toFixed(2))),
      values: ordenados.map(([, datos]) => Number(datos.valor.toFixed(2))),
    };
  }, [filteredMovimientos]);

  const graficoPorOrigen = useMemo<ChartData>(() => {
    const totales = new Map<string, { cantidad: number; valor: number }>();
    filteredMovimientos.forEach((mov) => {
      const origen = mov.envia?.trim() || "Sin origen";
      const cantidad = Number(mov.cantidad ?? 0);
      const valor = Number(mov.costo_total_con_iva ?? 0);
      const entry = totales.get(origen) ?? { cantidad: 0, valor: 0 };
      entry.cantidad += cantidad;
      entry.valor += valor;
      totales.set(origen, entry);
    });
    const ordenados = Array.from(totales.entries()).sort((a, b) => b[1].valor - a[1].valor);
    return {
      labels: ordenados.map(([origen]) => origen),
      series: ordenados.map(([, datos]) => Number(datos.cantidad.toFixed(2))),
      values: ordenados.map(([, datos]) => Number(datos.valor.toFixed(2))),
    };
  }, [filteredMovimientos]);

  const graficoPorSku = useMemo<ChartData>(() => {
    const totales = new Map<string, { cantidad: number; valor: number }>();
    filteredMovimientos.forEach((mov) => {
      const clave =
        (mov.descripcion?.trim() && `${mov.descripcion?.trim()}`) ||
        (mov.idarticulo?.trim() && `SKU ${mov.idarticulo?.trim()}`) ||
        "Sin descripción";
      const cantidad = Number(mov.cantidad ?? 0);
      const valor = Number(mov.costo_total_con_iva ?? 0);
      const entry = totales.get(clave) ?? { cantidad: 0, valor: 0 };
      entry.cantidad += cantidad;
      entry.valor += valor;
      totales.set(clave, entry);
    });
    const ordenados = Array.from(totales.entries()).sort((a, b) => b[1].valor - a[1].valor);
    return {
      labels: ordenados.map(([sku]) => sku),
      series: ordenados.map(([, datos]) => Number(datos.cantidad.toFixed(2))),
      values: ordenados.map(([, datos]) => Number(datos.valor.toFixed(2))),
    };
  }, [filteredMovimientos]);

  const destinosKpis = useMemo<KpiTabItem[]>(() => {
    const destinoPrincipal = graficoPorRecibe.labels[0] ?? "Sin datos";
    const destinoPrincipalValor = graficoPorRecibe.series[0] ?? 0;
    const tipoDominante = graficoPorTipo.labels[0] ?? "Sin datos";
    const tipoDominanteValor = graficoPorTipo.series[0] ?? 0;
    const { display: destinoDisplay, tooltip: destinoTooltip } = truncateDisplayValue(destinoPrincipal, 26);
    const { display: tipoDisplay, tooltip: tipoTooltip } = truncateDisplayValue(tipoDominante, 26);
    const totalDestinosUnidades = graficoPorRecibe.series.reduce((acc, valor) => acc + valor, 0);
    const topDestinosParticipacion =
      totalDestinosUnidades > 0
        ? (graficoPorRecibe.series.slice(0, 5).reduce((acc, valor) => acc + valor, 0) / totalDestinosUnidades) * 100
        : 0;

    return [
      {
        label: modeCopy.destinationLabel,
        value: resumenStats.destinosActivos.toLocaleString("es-PY"),
        detalle: destinationSummaryDetail,
        icono: "ri-store-3-line",
        color: "success",
      },
      {
        label: destinationAverageLabel,
        value: resumenStats.promedioPorDestino.toLocaleString("es-PY", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        }),
        detalle: destinationAverageDetail,
        icono: "ri-function-line",
        color: "info",
      },
      {
        label: destinationMainLabel,
        value: destinoDisplay,
        valueTooltip: destinoTooltip,
        detalle:
          destinoPrincipalValor > 0
            ? `${destinoPrincipalValor.toLocaleString("es-PY")} unidades`
            : "Sin información disponible",
        icono: "ri-map-pin-2-line",
        color: "danger",
      },
      {
        label: "Tipo dominante",
        value: tipoDisplay,
        valueTooltip: tipoTooltip,
        detalle:
          tipoDominanteValor > 0
            ? `${tipoDominanteValor.toLocaleString("es-PY")} unidades`
            : "Sin información disponible",
        icono: "ri-git-merge-line",
        color: "primary",
      },
      {
        label: "Top 5 participación",
        value: `${topDestinosParticipacion.toFixed(1)}%`,
        detalle: destinationTopDetail,
        icono: "ri-pie-chart-2-line",
        color: "secondary",
      },
    ];
  }, [
    destinationAverageDetail,
    destinationAverageLabel,
    destinationMainLabel,
    destinationSummaryDetail,
    destinationTopDetail,
    graficoPorRecibe,
    graficoPorTipo,
    modeCopy.destinationLabel,
    resumenStats,
  ]);

  const origenesKpis = useMemo<KpiTabItem[]>(() => {
    const origenPrincipal = graficoPorOrigen.labels[0] ?? "Sin datos";
    const origenPrincipalValor = graficoPorOrigen.series[0] ?? 0;
    const promedioUnidadesPorOrigen =
      resumenStats.origenesActivos > 0 ? resumenStats.totalCantidad / resumenStats.origenesActivos : 0;
    const promedioMovimientosPorOrigen =
      resumenStats.origenesActivos > 0 ? resumenStats.totalMovimientos / resumenStats.origenesActivos : 0;
    const { display: origenDisplay, tooltip: origenTooltip } = truncateDisplayValue(origenPrincipal, 26);
    const totalOrigenesUnidades = graficoPorOrigen.series.reduce((acc, valor) => acc + valor, 0);
    const topOrigenesParticipacion =
      totalOrigenesUnidades > 0
        ? (graficoPorOrigen.series.slice(0, 5).reduce((acc, valor) => acc + valor, 0) / totalOrigenesUnidades) * 100
        : 0;

    return [
      {
        label: originGroupLabel,
        value: resumenStats.origenesActivos.toLocaleString("es-PY"),
        detalle: originSummaryDetail,
        icono: "ri-store-2-line",
        color: "primary",
      },
      {
        label: "Promedio unidades",
        value: promedioUnidadesPorOrigen.toLocaleString("es-PY", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        }),
        detalle: originUnitsDetail,
        icono: "ri-equalizer-line",
        color: "warning",
      },
      {
        label: "Promedio movimientos",
        value: promedioMovimientosPorOrigen.toLocaleString("es-PY", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        }),
        detalle: originMovDetail,
        icono: "ri-node-tree",
        color: "success",
      },
      {
        label: originMainLabel,
        value: origenDisplay,
        valueTooltip: origenTooltip,
        detalle:
          origenPrincipalValor > 0
            ? `${origenPrincipalValor.toLocaleString("es-PY")} unidades`
            : "Sin información disponible",
        icono: "ri-map-pin-line",
        color: "danger",
      },
      {
        label: "Top 5 participación",
        value: `${topOrigenesParticipacion.toFixed(1)}%`,
        detalle: originTopDetail,
        icono: "ri-trophy-line",
        color: "secondary",
      },
    ];
  }, [
    graficoPorOrigen,
    originGroupLabel,
    originMainLabel,
    originMovDetail,
    originSummaryDetail,
    originTopDetail,
    originUnitsDetail,
    resumenStats,
  ]);

  const skusKpis = useMemo<KpiTabItem[]>(() => {
    const skuPrincipal = graficoPorSku.labels[0] ?? "Sin datos";
    const skuPrincipalValor = graficoPorSku.series[0] ?? 0;
    const totalUnidades = graficoPorSku.series.reduce((acc, valor) => acc + valor, 0);
    const topCincoParticipacion =
      graficoPorSku.series.slice(0, 5).reduce((acc, valor) => acc + valor, 0) / (totalUnidades || 1);
    const promedioPorSku = resumenStats.skuUnicos > 0 ? totalUnidades / resumenStats.skuUnicos : 0;
    const { display: skuDisplay, tooltip: skuTooltip } = truncateDisplayValue(skuPrincipal, 26);

    return [
      {
        label: skuUniqueLabel,
        value: resumenStats.skuUnicos.toLocaleString("es-PY"),
        detalle: skuUniqueDetail,
        icono: "ri-barcode-line",
        color: "info",
      },
      {
        label: skuLeaderLabel,
        value: skuDisplay,
        valueTooltip: skuTooltip,
        detalle:
          skuPrincipalValor > 0
            ? `${skuPrincipalValor.toLocaleString("es-PY")} unidades`
            : "Sin información disponible",
        icono: "ri-star-smile-line",
        color: "warning",
      },
      {
        label: "Unidades totales",
        value: totalUnidades.toLocaleString("es-PY", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        }),
        detalle: skuUnitsTotalDetail,
        icono: "ri-stack-line",
        color: "primary",
      },
      {
        label: "Top 5 participación",
        value: `${(topCincoParticipacion * 100).toFixed(1)}%`,
        detalle: skuTopDetail,
        icono: "ri-pie-chart-2-line",
        color: "danger",
      },
      {
        label: "Promedio por SKU",
        value: promedioPorSku.toLocaleString("es-PY", {
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        }),
        detalle: skuAverageDetail,
        icono: "ri-slideshow-line",
        color: "secondary",
      },
    ];
  }, [graficoPorSku, resumenStats, skuAverageDetail, skuLeaderLabel, skuTopDetail, skuUniqueDetail, skuUniqueLabel, skuUnitsTotalDetail]);

  const kpiTabs = useMemo<KpiTabConfig[]>(
    () => [
      { id: "resumen", label: "Resumen", items: resumenKpis },
      { id: "destinos", label: destinationGroupLabel, items: destinosKpis },
      { id: "origenes", label: originGroupLabel, items: origenesKpis },
      { id: "skus", label: skuGroupLabel, items: skusKpis },
    ],
    [destinationGroupLabel, destinosKpis, originGroupLabel, origenesKpis, resumenKpis, skuGroupLabel, skusKpis],
  );

  const chartTabsData = useMemo<ChartTabConfig[]>(
    () => [
      {
        id: "destinos",
        label: destinationGroupLabel,
        charts: buildChartCards(graficoPorRecibe, {
          idPrefix: "destinos",
          palette: ["#4b93ff", "#f97316", "#22d3ee", "#34d399", "#facc15", "#f43f5e"],
          distributionType: "donut",
          distributionTitle: destinationDistributionTitle,
          distributionSubtitle: destinationDistributionSubtitle,
          rankingTitle: destinationRankingTitle,
          rankingSubtitle: destinationRankingSubtitle,
          radialTitle: destinationRadialTitle,
          radialSubtitle: "Participación sobre el total",
          donutTitle: "Top 5 vs resto",
          donutSubtitle: destinationDonutSubtitle,
          evolutionTitle: destinationEvolutionTitle,
          evolutionSubtitle: destinationEvolutionSubtitle,
          layouts: [
            { colProps: { xs: 12, md: 6 }, cardClassName: "bg-soft-primary" },
            { colProps: { xs: 12, md: 6 }, cardClassName: "bg-soft-success" },
            { colProps: { xs: 12, md: 6 }, cardClassName: "bg-soft-warning" },
            { colProps: { xs: 12, md: 6 }, cardClassName: "bg-soft-info" },
            { colProps: { xs: 12 }, cardClassName: "bg-soft-danger" },
          ],
        }),
      },
      {
        id: "tipos",
        label: "Tipos",
        charts: buildChartCards(graficoPorTipo, {
          idPrefix: "tipos",
          palette: ["#a855f7", "#f97316", "#2563eb", "#06b6d4", "#f43f5e", "#65a30d"],
          distributionType: "polarArea",
          distributionTitle: "Distribución por tipo",
          distributionSubtitle: "Movimientos registrados",
          rankingTitle: "Top 5 tipos",
          rankingSubtitle: "Unidades involucradas",
          radialTitle: "Tipo líder",
          radialSubtitle: "Participación sobre el total",
          donutTitle: "Top 5 vs resto",
          donutSubtitle: "Comparativa de tipos",
          evolutionTitle: "Tendencia de tipos",
          evolutionSubtitle: "Top 10 tipos por unidades",
          layouts: [
            { colProps: { xs: 12, md: 6 }, cardClassName: "bg-soft-success" },
            { colProps: { xs: 12, md: 6 }, cardClassName: "bg-soft-primary" },
            { colProps: { xs: 12, md: 6 }, cardClassName: "bg-soft-warning" },
            { colProps: { xs: 12, md: 6 }, cardClassName: "bg-soft-info" },
            { colProps: { xs: 12 }, cardClassName: "bg-soft-danger" },
          ],
        }),
      },
      {
        id: "origenes",
        label: originGroupLabel,
        charts: buildChartCards(graficoPorOrigen, {
          idPrefix: "origenes",
          palette: ["#f97316", "#60a5fa", "#a855f7", "#14b8a6", "#f87171", "#facc15"],
          distributionType: "donut",
          distributionTitle: originDistributionTitle,
          distributionSubtitle: originDistributionSubtitle,
          rankingTitle: originRankingTitle,
          rankingSubtitle: originRankingSubtitle,
          radialTitle: originRadialTitle,
          radialSubtitle: "Participación sobre el total",
          donutTitle: "Top 5 vs resto",
          donutSubtitle: originDonutSubtitle,
          evolutionTitle: originEvolutionTitle,
          evolutionSubtitle: originEvolutionSubtitle,
          layouts: [
            { colProps: { xs: 12, md: 6 }, cardClassName: "bg-soft-warning" },
            { colProps: { xs: 12, md: 6 }, cardClassName: "bg-soft-danger" },
            { colProps: { xs: 12, md: 6 }, cardClassName: "bg-soft-primary" },
            { colProps: { xs: 12, md: 6 }, cardClassName: "bg-soft-success" },
            { colProps: { xs: 12 }, cardClassName: "bg-soft-info" },
          ],
        }),
      },
      {
        id: "skus",
        label: skuGroupLabel,
        charts: buildChartCards(graficoPorSku, {
          idPrefix: "skus",
          palette: ["#22d3ee", "#a855f7", "#f97316", "#38bdf8", "#f43f5e", "#4ade80"],
          distributionType: "donut",
          distributionTitle: skuDistributionTitle,
          distributionSubtitle: skuDistributionSubtitle,
          rankingTitle: skuRankingTitle,
          rankingSubtitle: skuRankingSubtitle,
          radialTitle: skuRadialTitle,
          radialSubtitle: "Participación sobre el total",
          donutTitle: "Top 5 vs resto",
          donutSubtitle: skuComparisonDetail,
          evolutionTitle: skuEvolutionTitle,
          evolutionSubtitle: skuEvolutionSubtitle,
          layouts: [
            { colProps: { xs: 12, md: 6 }, cardClassName: "bg-soft-info" },
            { colProps: { xs: 12, md: 6 }, cardClassName: "bg-soft-primary" },
            { colProps: { xs: 12, md: 6 }, cardClassName: "bg-soft-success" },
            { colProps: { xs: 12, md: 6 }, cardClassName: "bg-soft-warning" },
            { colProps: { xs: 12 }, cardClassName: "bg-soft-danger" },
          ],
        }),
      },
    ],
    [
      destinationDistributionSubtitle,
      destinationDistributionTitle,
      destinationDonutSubtitle,
      destinationEvolutionSubtitle,
      destinationEvolutionTitle,
      destinationGroupLabel,
      destinationRadialTitle,
      destinationRankingSubtitle,
      destinationRankingTitle,
      graficoPorOrigen,
      graficoPorRecibe,
      graficoPorSku,
      graficoPorTipo,
      originDistributionSubtitle,
      originDistributionTitle,
      originDonutSubtitle,
      originEvolutionSubtitle,
      originEvolutionTitle,
      originGroupLabel,
      originRadialTitle,
      originRankingSubtitle,
      originRankingTitle,
      skuComparisonDetail,
      skuDistributionSubtitle,
      skuDistributionTitle,
      skuEvolutionSubtitle,
      skuEvolutionTitle,
      skuGroupLabel,
      skuRadialTitle,
      skuRankingSubtitle,
      skuRankingTitle,
    ],
  );

  const handleTiposChange = useCallback((next: readonly InventoryFilterOption[]) => {
    setSelectedTipos([...next]);
  }, []);
  const handleIdMovChange = useCallback((next: readonly InventoryFilterOption[]) => {
    setSelectedIdMovs([...next]);
  }, []);
  const handleFamiliasChange = useCallback((next: readonly InventoryFilterOption[]) => {
    setSelectedFamilias([...next]);
  }, []);
  const handleLineasChange = useCallback((next: readonly InventoryFilterOption[]) => {
    setSelectedLineas([...next]);
  }, []);
  const handleServersChange = useCallback((next: readonly InventoryFilterOption[]) => {
    setSelectedServers([...next]);
  }, []);
  const handleRecibesChange = useCallback((next: readonly InventoryFilterOption[]) => {
    setSelectedRecibes([...next]);
  }, []);
  const handleDescripcionesChange = useCallback((next: readonly InventoryFilterOption[]) => {
    setSelectedDescripciones([...next]);
  }, []);
  const handleOpenDetalle = useCallback((view: AggregatedViewId, label: string) => {
    setDetalleContext({ view, label });
  }, []);
  const handleCloseDetalle = useCallback(() => {
    setDetalleContext(null);
  }, []);

  const fetchMovimientos = useCallback(async () => {
    if (!fechaDesde || !fechaHasta) {
      return;
    }
    setLoadingMovimientos(true);
    setErrorMovimientos(null);
    try {
      const params = new URLSearchParams({ fechaDesde, fechaHasta });
      const data = await requestJson<MovimientosStockResponse>(
        `${modeSettings.endpoint}?${params.toString()}`,
      );
      setMovimientos(data.detalle);
      setSelectedTipos([]);
      setSelectedIdMovs([]);
      setSelectedFamilias([]);
      setSelectedLineas([]);
      setSelectedServers([]);
      setSelectedRecibes([]);
      setSelectedDescripciones([]);
      setActiveCategory("all");
      setActiveTableTab("detalle");
      setHasFetched(true);
    } catch (error) {
      const mensaje =
        error instanceof Error ? error.message : "No pudimos obtener los movimientos de stock.";
      setErrorMovimientos(mensaje);
      setMovimientos([]);
      setHasFetched(true);
    } finally {
      setLoadingMovimientos(false);
    }
  }, [fechaDesde, fechaHasta, modeSettings.endpoint]);

  useEffect(() => {
    void fetchMovimientos();
  }, [fetchMovimientos]);

  const filtersDisabled = !hasFetched || movimientos.length === 0;
  const disableConsultar = !fechaDesde || !fechaHasta || loadingMovimientos;
  const initialLoading = !hasFetched;
  const emptyMessage = initialLoading ? "" : modeSettings.emptyMessage;

  const handleExportarExcel = useCallback(async () => {
    if (!hasFetched || filteredMovimientos.length === 0 || exportando) {
      return;
    }
    const formatExportDate = (
      valor?: string | Date | null,
      options?: { includeTime?: boolean },
    ) => {
      if (!valor) return "";
      const fecha =
        valor instanceof Date ? valor : new Date(String(valor));
      if (Number.isNaN(fecha.getTime())) {
        return String(valor ?? "").trim();
      }
      const pad = (numero: number) => numero.toString().padStart(2, "0");
      const datePart = `${pad(fecha.getDate())}/${pad(fecha.getMonth() + 1)}/${fecha.getFullYear()}`;
      if (options?.includeTime) {
        const hours = pad(fecha.getHours());
        const minutes = pad(fecha.getMinutes());
        const seconds = pad(fecha.getSeconds());
        return `${datePart} ${hours}:${minutes}:${seconds}`;
      }
      return datePart;
    };
    try {
      setExportando(true);
      const normalizeKey = (valor: string) => valor.replace(/[^a-z0-9]/gi, "").toLowerCase();
      const filasExportar = filteredMovimientos.map((mov) => {
        const anyMov = mov as MovimientoStockApiRow & Record<string, unknown>;
        const normalizedKeyMap = new Map<string, string>();
        Object.keys(anyMov).forEach((rawKey) => {
          const normalized = normalizeKey(rawKey);
          if (normalized && !normalizedKeyMap.has(normalized)) {
            normalizedKeyMap.set(normalized, rawKey);
          }
        });
        const obtenerValor = (clave: string, fallback?: string) => {
          const normalized = normalizeKey(clave);
          const resolvedKey = normalizedKeyMap.get(normalized);
          const valor =
            (resolvedKey ? (anyMov as Record<string, unknown>)[resolvedKey] : undefined) ??
            anyMov[clave as keyof MovimientoStockApiRow] ??
            (anyMov[clave.toUpperCase()] as unknown) ??
            fallback ??
            "";
          const texto = typeof valor === "string" ? valor : String(valor ?? "");
          return texto.trim();
        };
        const factura = obtenerValor("factura", anyMov.periodo_texto ?? "");
        const nroFactura = obtenerValor("nro_factura");
        return {
          SERVER_ORIGEN: mov.server ?? mov.fuente ?? "",
          BARCODE: mov.barcode ?? "",
          FECHA: formatExportDate(mov.fecha),
          FECHA_CARGA: formatExportDate(mov.fecha_carga, { includeTime: true }),
          IDMOVIMIENTO:
            mov.idmovimiento != null && Number.isFinite(mov.idmovimiento) ? mov.idmovimiento : "",
          FACTURA: factura,
          NRO_FACTURA: nroFactura,
          TIPOMOVIMIENTO: mov.tipo_movimiento ?? "",
          CONDICION: obtenerValor("condicion"),
          IDDEPOSITO: Number.isFinite(Number(obtenerValor("iddeposito"))) ? obtenerValor("iddeposito") : "",
          DEPOSITO: obtenerValor("deposito"),
          IDPERSONA: Number.isFinite(Number(obtenerValor("idpersona"))) ? obtenerValor("idpersona") : "",
          PERSONA: obtenerValor("persona"),
          IDARTICULO: mov.idarticulo ?? "",
          ARTICULO: obtenerValor("articulo", mov.descripcion ?? ""),
          PRESENTACION: obtenerValor("presentacion"),
          FAMILIA: mov.familia ?? "",
          LINEA: mov.linea ?? "",
          USUARIO: obtenerValor("usuario"),
          CANTIDAD: mov.cantidad ?? "",
          IDMONEDA: mov.idmoneda ?? "",
          COSTO_UNIT_SIN_IVA: mov.costo_unit_sin_iva ?? "",
          COSTO_UNIT_CON_IVA: mov.costo_unit_con_iva ?? "",
          IMPUESTO: mov.impuesto ?? "",
          EXENTA: mov.exenta ?? "",
          GRAVADA: mov.gravada ?? "",
          IMPORTE_TOTAL: mov.importe_total ?? mov.costo_total_con_iva ?? "",
          NOTA: mov.nota ?? "",
        };
      });
      const worksheet = XLSX.utils.json_to_sheet(filasExportar);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, exportSheetName);
      const prefijo = exportPrefix;
      const nombreArchivo = `${prefijo}_${fechaDesde}_al_${fechaHasta}.xlsx`;
      XLSX.writeFile(workbook, nombreArchivo);
    } finally {
      setExportando(false);
    }
  }, [exportPrefix, exportSheetName, exportando, filteredMovimientos, fechaDesde, fechaHasta, hasFetched]);

  const formatCurrencyValue = useCallback(
    (valor: number | null) => (valor == null || Number.isNaN(Number(valor)) ? "—" : `Gs. ${formatMoneda(Number(valor))}`),
    [],
  );

  const renderDoubleMetric = useCallback(
    (sinIva: number | null, conIva: number | null) => (
      <div className="text-end">
        <div className="fw-semibold text-body">{formatCurrencyValue(sinIva)}</div>
        <small className="text-muted">Con IVA: {formatCurrencyValue(conIva)}</small>
      </div>
    ),
    [formatCurrencyValue],
  );

  const detailColumns = useMemo(
    () => [
      {
        header: "Fecha",
        accessorKey: "fecha",
        cell: (cell: any) => <div className="fw-semibold text-body">{cell.getValue()}</div>,
      },
      {
        header: tipoColumnHeader,
        accessorKey: "tipoMovimiento",
        cell: (cell: any) => (
          <div>
            <div className="text-body fw-semibold">{cell.getValue()}</div>
            <small className="text-muted">{cell.row.original.movimientoInfo}</small>
          </div>
        ),
      },
      {
        header: periodoHeader,
        accessorKey: "periodoCosto",
        cell: (cell: any) => <div className="text-muted">{cell.getValue()}</div>,
      },
      {
        header: destinoColumnHeader,
        accessorKey: "envia",
        cell: (cell: any) => (
          <div>
            <div className="fw-semibold">{cell.getValue()}</div>
            <small className="text-muted">
              {destinoSecondaryLabel}: {cell.row.original.recibe}
            </small>
          </div>
        ),
      },
      {
        header: "Familia / Línea",
        accessorKey: "familyName",
        cell: (cell: any) => (
          <div>
            <div className="fw-semibold">{cell.row.original.familyName || "Sin familia"}</div>
            {cell.row.original.lineName ? (
              <small className="text-muted">Línea: {cell.row.original.lineName}</small>
            ) : (
              <small className="text-muted">Línea: Sin datos</small>
            )}
          </div>
        ),
      },
      {
        header: "Artículo",
        accessorKey: "articuloInfo",
        cell: (cell: any) => {
          const row = cell.row.original as InventoryRow;
          const [principal, ...resto] = row.articuloLineas;
          return (
            <div>
              <div className="fw-semibold">{principal || "Sin descripción"}</div>
              {resto.map((linea, index) => (
                <div key={`${row.articuloInfo}-${index}`} className="text-muted small">
                  {linea}
                </div>
              ))}
            </div>
          );
        },
      },
      {
        header: "Cantidad",
        accessorKey: "cantidad",
        cell: (cell: any) => (
          <div className="text-end fw-semibold">
            {Number(cell.getValue() ?? 0).toLocaleString("es-PY", {
              minimumFractionDigits: 0,
              maximumFractionDigits: 2,
            })}
          </div>
        ),
      },
      {
        header: costoUnitarioHeader,
        accessorKey: "costUnitSinIva",
        cell: (cell: any) => {
          const row = cell.row.original as InventoryRow;
          return renderDoubleMetric(row.costUnitSinIva, row.costUnitConIva);
        },
      },
      {
        header: valorHeader,
        accessorKey: "valorSinIva",
        cell: (cell: any) => {
          const row = cell.row.original as InventoryRow;
          return renderDoubleMetric(row.valorSinIva, row.valorConIva);
        },
      },
    ],
    [costoUnitarioHeader, destinoColumnHeader, destinoSecondaryLabel, periodoHeader, renderDoubleMetric, tipoColumnHeader, valorHeader],
  );

  const detailDataTableColumns = useMemo<TableColumn<InventoryRow>[]>(
    () => [
      {
        name: "Fecha",
        selector: (row) => row.fecha ?? "-",
        sortable: true,
        wrap: true,
        cell: (row) => <div className="fw-semibold text-body">{row.fecha || "Sin fecha"}</div>,
      },
      {
        name: tipoColumnHeader,
        selector: (row) => row.tipoMovimiento ?? "-",
        sortable: true,
        wrap: true,
        cell: (row) => (
          <div>
            <div className="text-body fw-semibold">{row.tipoMovimiento || "Sin tipo"}</div>
            <small className="text-muted">{row.movimientoInfo || "Sin información"}</small>
          </div>
        ),
      },
      {
        name: periodoHeader,
        selector: (row) => row.periodoCosto ?? "-",
        sortable: true,
        wrap: true,
        cell: (row) => <div className="text-muted">{row.periodoCosto || "Sin periodo"}</div>,
      },
      {
        name: destinoColumnHeader,
        selector: (row) => row.envia ?? row.recibe ?? "-",
        sortable: true,
        wrap: true,
        cell: (row) => (
          <div>
            <div className="fw-semibold">{row.envia || "Sin datos"}</div>
            <small className="text-muted">
              {destinoSecondaryLabel}: {row.recibe || "Sin datos"}
            </small>
          </div>
        ),
      },
      {
        name: "Familia / Línea",
        selector: (row) => row.familyName ?? "Sin familia",
        sortable: true,
        wrap: true,
        cell: (row) => (
          <div>
            <div className="fw-semibold">{row.familyName || "Sin familia"}</div>
            <small className="text-muted">Línea: {row.lineName || "Sin datos"}</small>
          </div>
        ),
      },
      {
        name: "Artículo",
        selector: (row) => row.articuloInfo ?? "Sin descripción",
        sortable: true,
        wrap: true,
        cell: (row) => {
          const [principal, ...resto] = row.articuloLineas;
          return (
            <div>
              <div className="fw-semibold">{principal || row.articuloInfo || "Sin descripción"}</div>
              {resto.map((linea, index) => (
                <div key={`${row.articuloInfo}-${index}`} className="text-muted small">
                  {linea}
                </div>
              ))}
            </div>
          );
        },
      },
      {
        name: "Cantidad",
        selector: (row) => Number(row.cantidad ?? 0),
        sortable: true,
        right: true,
        cell: (row) => (
          <div className="text-end fw-semibold">
            {Number(row.cantidad ?? 0).toLocaleString("es-PY", {
              minimumFractionDigits: 0,
              maximumFractionDigits: 2,
            })}
          </div>
        ),
      },
      {
        name: costoUnitarioHeader,
        selector: (row) => Number(row.costUnitSinIva ?? 0),
        sortable: true,
        right: true,
        cell: (row) => renderDoubleMetric(row.costUnitSinIva, row.costUnitConIva),
      },
      {
        name: valorHeader,
        selector: (row) => Number(row.valorSinIva ?? 0),
        sortable: true,
        right: true,
        cell: (row) => renderDoubleMetric(row.valorSinIva, row.valorConIva),
      },
    ],
    [
      costoUnitarioHeader,
      destinoColumnHeader,
      destinoSecondaryLabel,
      periodoHeader,
      renderDoubleMetric,
      tipoColumnHeader,
      valorHeader,
    ],
  );

  const aggregatedDataTableColumns = useMemo(
    () => ({
      depositos: [
        {
          name: isInventarios ? "Sucursal / Depósito" : "Depósito",
          selector: (row: AggregatedRow) => row.etiqueta ?? "Sin datos",
          sortable: true,
          wrap: true,
          cell: (row: AggregatedRow) => <div className="fw-semibold text-body">{row.etiqueta || "Sin datos"}</div>,
        },
        {
          name: "Cantidad",
          selector: (row: AggregatedRow) => Number(row.cantidad ?? 0),
          sortable: true,
          right: true,
          cell: (row: AggregatedRow) => (
            <div className="text-end fw-semibold">
              {Number(row.cantidad ?? 0).toLocaleString("es-PY", {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
              })}
            </div>
          ),
        },
        {
          name: valorHeader,
          selector: (row: AggregatedRow) => Number(row.valorSinIva ?? 0),
          sortable: true,
          right: true,
          cell: (row: AggregatedRow) => renderDoubleMetric(row.valorSinIva, row.valorConIva),
        },
        {
          name: "Detalle",
          button: true,
          cell: (row: AggregatedRow) => (
            <div className="text-center">
              <a
                href="#!"
                className="text-primary fw-semibold text-decoration-underline"
                onClick={(event) => {
                  event.preventDefault();
                  handleOpenDetalle("depositos", row.etiqueta);
                }}
              >
                Ver detalle
              </a>
            </div>
          ),
          ignoreRowClick: true,
        },
      ],
      familias: [
        {
          name: "Familia",
          selector: (row: AggregatedRow) => row.etiqueta ?? "Sin familia",
          sortable: true,
          wrap: true,
          cell: (row: AggregatedRow) => <div className="fw-semibold text-body">{row.etiqueta || "Sin familia"}</div>,
        },
        {
          name: "Cantidad",
          selector: (row: AggregatedRow) => Number(row.cantidad ?? 0),
          sortable: true,
          right: true,
          cell: (row: AggregatedRow) => (
            <div className="text-end fw-semibold">
              {Number(row.cantidad ?? 0).toLocaleString("es-PY", {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
              })}
            </div>
          ),
        },
        {
          name: valorHeader,
          selector: (row: AggregatedRow) => Number(row.valorSinIva ?? 0),
          sortable: true,
          right: true,
          cell: (row: AggregatedRow) => renderDoubleMetric(row.valorSinIva, row.valorConIva),
        },
        {
          name: "Detalle",
          button: true,
          cell: (row: AggregatedRow) => (
            <div className="text-center">
              <a
                href="#!"
                className="text-primary fw-semibold text-decoration-underline"
                onClick={(event) => {
                  event.preventDefault();
                  handleOpenDetalle("familias", row.etiqueta);
                }}
              >
                Ver detalle
              </a>
            </div>
          ),
          ignoreRowClick: true,
        },
      ],
      articulos: [
        {
          name: isInventoryLike ? "Artículo" : "SKU",
          selector: (row: AggregatedRow) => row.etiqueta ?? "Sin datos",
          sortable: true,
          wrap: true,
          cell: (row: AggregatedRow) => <div className="fw-semibold text-body">{row.etiqueta || "Sin datos"}</div>,
        },
        {
          name: "Cantidad",
          selector: (row: AggregatedRow) => Number(row.cantidad ?? 0),
          sortable: true,
          right: true,
          cell: (row: AggregatedRow) => (
            <div className="text-end fw-semibold">
              {Number(row.cantidad ?? 0).toLocaleString("es-PY", {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
              })}
            </div>
          ),
        },
        {
          name: valorHeader,
          selector: (row: AggregatedRow) => Number(row.valorSinIva ?? 0),
          sortable: true,
          right: true,
          cell: (row: AggregatedRow) => renderDoubleMetric(row.valorSinIva, row.valorConIva),
        },
        {
          name: "Detalle",
          button: true,
          cell: (row: AggregatedRow) => (
            <div className="text-center">
              <a
                href="#!"
                className="text-primary fw-semibold text-decoration-underline"
                onClick={(event) => {
                  event.preventDefault();
                  handleOpenDetalle("articulos", row.etiqueta);
                }}
              >
                Ver detalle
              </a>
            </div>
          ),
          ignoreRowClick: true,
        },
      ],
      proveedores: !isCompras
        ? []
        : [
        {
          name: "Proveedor / Depósito",
          selector: (row: AggregatedRow) => row.etiqueta ?? "Sin proveedor",
          sortable: true,
          wrap: true,
          cell: (row: AggregatedRow) => <div className="fw-semibold text-body">{row.etiqueta || "Sin proveedor"}</div>,
        },
        {
          name: "Cantidad",
          selector: (row: AggregatedRow) => Number(row.cantidad ?? 0),
          sortable: true,
          right: true,
          cell: (row: AggregatedRow) => (
            <div className="text-end fw-semibold">
              {Number(row.cantidad ?? 0).toLocaleString("es-PY", {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
              })}
            </div>
          ),
        },
        {
          name: valorHeader,
          selector: (row: AggregatedRow) => Number(row.valorSinIva ?? 0),
          sortable: true,
          right: true,
          cell: (row: AggregatedRow) => renderDoubleMetric(row.valorSinIva, row.valorConIva),
        },
        {
          name: "Detalle",
          button: true,
          cell: (row: AggregatedRow) => (
            <div className="text-center">
              <a
                href="#!"
                className="text-primary fw-semibold text-decoration-underline"
                onClick={(event) => {
                  event.preventDefault();
                  handleOpenDetalle("proveedores", row.etiqueta);
                }}
              >
                Ver detalle
              </a>
            </div>
          ),
          ignoreRowClick: true,
        },
      ],
    }),
    [handleOpenDetalle, isCompras, isInventarios, isInventoryLike, renderDoubleMetric, valorHeader],
  );

  const aggregatedData = useMemo(
    () => ({
      depositos: aggregateInventoryRows(
        normalizedRows,
        (row) => row.recibe?.trim() || row.envia?.trim() || "Sin depósito",
      ),
      familias: aggregateInventoryRows(normalizedRows, (row) => row.familyName?.trim() || "Sin familia"),
      articulos: aggregateInventoryRows(normalizedRows, (row) => row.articuloEtiqueta || "Sin artículo"),
      proveedores: isCompras
        ? aggregateInventoryRows(normalizedRows, (row) => row.envia?.trim() || "Sin proveedor")
        : [],
    }),
    [isCompras, normalizedRows],
  );

  const tableViews = useMemo<InventoryStatusTab[]>(
    () => [
      {
        id: "detalle",
        label: "Listado",
        badgeCount: normalizedRows.length,
        badgeColor: "bg-success-subtle text-success",
      },
      {
        id: "depositos",
        label: destinationGroupLabel,
        badgeCount: aggregatedData.depositos.length,
      },
      {
        id: "familias",
        label: "Familias",
        badgeCount: aggregatedData.familias.length,
      },
      {
        id: "articulos",
        label: skuGroupLabel,
        badgeCount: aggregatedData.articulos.length,
      },
      ...(isCompras
        ? [
            {
              id: "proveedores",
              label: "Proveedores",
              badgeCount: aggregatedData.proveedores.length,
            } as InventoryStatusTab,
          ]
        : []),
    ],
    [
      aggregatedData.articulos.length,
      aggregatedData.depositos.length,
      aggregatedData.familias.length,
      aggregatedData.proveedores.length,
      destinationGroupLabel,
      normalizedRows.length,
      skuGroupLabel,
      isCompras,
    ],
  );

  const currentDataTableColumns = useMemo(() => {
    switch (activeTableTab) {
      case "depositos":
        return aggregatedDataTableColumns.depositos;
      case "familias":
        return aggregatedDataTableColumns.familias;
      case "articulos":
        return aggregatedDataTableColumns.articulos;
      case "proveedores":
        return aggregatedDataTableColumns.proveedores;
      default:
        return detailDataTableColumns;
    }
  }, [activeTableTab, aggregatedDataTableColumns, detailDataTableColumns]);

  const tableData = useMemo(() => {
    switch (activeTableTab) {
      case "depositos":
        return aggregatedData.depositos;
      case "familias":
        return aggregatedData.familias;
      case "articulos":
        return aggregatedData.articulos;
      case "proveedores":
        return aggregatedData.proveedores;
      default:
        return normalizedRows;
    }
  }, [
    activeTableTab,
    aggregatedData.articulos,
    aggregatedData.depositos,
    aggregatedData.familias,
    aggregatedData.proveedores,
    normalizedRows,
  ]);

  const detalleRows = useMemo(() => {
    if (!detalleContext) return [];
    const etiqueta = detalleContext.label?.trim() ?? "";
    if (etiqueta.length === 0) {
      return [];
    }
    const etiquetaLower = etiqueta.toLowerCase();
    switch (detalleContext.view) {
      case "depositos":
        return normalizedRows.filter((row) => {
          const envia = (row.envia || "").toLowerCase();
          const recibe = (row.recibe || "").toLowerCase();
          return envia === etiquetaLower || recibe === etiquetaLower;
        });
      case "familias":
        return normalizedRows.filter((row) => (row.familyName || "").toLowerCase() === etiquetaLower);
      case "articulos":
        return normalizedRows.filter(
          (row) =>
            row.articuloEtiqueta.toLowerCase() === etiquetaLower ||
            row.articuloInfo.toLowerCase().includes(etiquetaLower) ||
            row.movimientoInfo.toLowerCase().includes(etiquetaLower),
        );
      case "proveedores":
        return normalizedRows.filter((row) => (row.envia || "").toLowerCase() === etiquetaLower);
      default:
        return [];
    }
  }, [detalleContext, normalizedRows]);

  const detalleTitle = useMemo(() => {
    if (!detalleContext) return "";
    const referencias: Record<AggregatedViewId, string> = {
      depositos: destinationSingular,
      familias: "Familia",
      articulos: skuSingular,
      proveedores: "Proveedor",
    };
    return `${referencias[detalleContext.view]} · ${detalleContext.label}`;
  }, [detalleContext, destinationSingular, skuSingular]);

  const detalleDescription = useMemo(() => {
    if (!detalleContext) {
      return "Seleccioná una agrupación para ver el detalle de los movimientos.";
    }
    const mensajes: Record<AggregatedViewId, string> = {
      depositos: isInventarios
        ? "Registros de inventario asociados a la sucursal seleccionada"
        : isCompras
          ? "Compras asociadas al depósito seleccionado"
          : "Movimientos asociados al depósito seleccionado",
      familias: isInventoryLike
        ? "Registros vinculados a la familia elegida"
        : "Transferencias relacionadas con la familia elegida",
      articulos: isInventoryLike
        ? "Registros del artículo seleccionado"
        : "Movimientos del SKU seleccionado",
      proveedores: "Registros asociados al proveedor seleccionado",
    };
    return `${mensajes[detalleContext.view]} (${detalleRows.length.toLocaleString("es-PY")} registros).`;
  }, [detalleContext, detalleRows.length, isCompras, isInventarios, isInventoryLike]);

  const detalleTotals = useMemo(() => calcularTotalesMovimientos(detalleRows), [detalleRows]);
  const listadoTotals = useMemo(() => calcularTotalesMovimientos(normalizedRows), [normalizedRows]);

  const handleExportDetalleExcel = useCallback(() => {
    if (!detalleContext || detalleRows.length === 0 || exportandoDetalleExcel) {
      return;
    }
    try {
      setExportandoDetalleExcel(true);
      const filas = detalleRows.map((row) => ({
        fecha: row.fecha,
        tipo: row.tipoMovimiento,
        origen: row.envia,
        destino: row.recibe,
        familia: row.familyName || "Sin familia",
        linea: row.lineName || "Sin línea",
        articulo: row.articuloInfo,
        cantidad: row.cantidad,
        costo_unit_sin_iva: row.costUnitSinIva ?? "",
        costo_unit_con_iva: row.costUnitConIva ?? "",
        valor_sin_iva: row.valorSinIva ?? "",
        valor_con_iva: row.valorConIva ?? "",
        periodo_costo: row.periodoCosto,
      }));
      const sheet = XLSX.utils.json_to_sheet(filas);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, sheet, "Detalle");
      const etiqueta = sanitizeFileName(detalleContext.label || detalleContext.view);
      const nombreArchivo = `detalle_${detalleContext.view}_${etiqueta || "movimientos"}.xlsx`;
      XLSX.writeFile(workbook, nombreArchivo);
    } finally {
      setExportandoDetalleExcel(false);
    }
  }, [detalleContext, detalleRows, exportandoDetalleExcel]);

  const generarPdfMovimientos = useCallback(
    (filas: InventoryRow[], config: { title: string; description: string; fileName: string; totals: MovimientoTotals }) => {
      if (filas.length === 0) {
        return;
      }
      const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
      let pageWidth = doc.internal.pageSize.getWidth();
      let pageHeight = doc.internal.pageSize.getHeight();
      const marginX = 28;
      const marginY = 28;
      const lineHeight = 12;
      const tableFontSize = 8;
      const headerHeight = 110;
      const generadoPor = resolveUsuarioActual();
      const timestamp = new Date();
      const timestampLabel = timestamp.toLocaleString("es-PY", {
        dateStyle: "short",
        timeStyle: "medium",
      });
      let pageNumber = 1;
      let currentY = 0;
      doc.setFontSize(tableFontSize);

      type PdfLineEntry = {
        text: string;
        muted?: boolean;
      };

      type PdfColumnLayout = {
        label: string;
        width: number;
        value: (row: InventoryRow) => string;
        align?: "right";
        multiline?: boolean;
        getLineEntries?: (row: InventoryRow) => PdfLineEntry[];
        x?: number;
      };

      const origenDestinoLabel = isInventarios ? "Sucursal" : isCompras ? "Proveedor/Depósito" : "Origen/Destino";
      const columnsPdf: PdfColumnLayout[] = [
        {
          label: "Fecha / Tipo",
          width: 110,
          value: (row) => row.fecha || "-",
          multiline: true,
          getLineEntries: (row) => [
            { text: row.fecha || "-" },
            { text: `Tipo: ${row.tipoMovimiento || "-"}`, muted: true },
            { text: `Server: ${row.server || "-"}`, muted: true },
          ],
        },
        {
          label: origenDestinoLabel,
          width: 110,
          value: (row) => row.envia || "-",
          multiline: true,
          getLineEntries: (row) => [
            {
              text: isInventarios
                ? `Sucursal: ${row.recibe || "-"}`
                : isCompras
                  ? `Proveedor: ${row.envia || "-"}`
                  : `Origen: ${row.envia || "-"}`,
            },
            {
              text: isInventarios
                ? `Depósito: ${row.envia || "-"}`
                : isCompras
                  ? `Depósito: ${row.recibe || "-"}`
                  : `Destino: ${row.recibe || "-"}`,
              muted: true,
            },
          ],
        },
        {
          label: "Artículo",
          width: 140,
          value: (row) => row.articuloLineas[0] || row.articuloInfo || "Sin descripción",
          multiline: true,
          getLineEntries: (row) =>
            row.articuloLineas.map((texto, index) => ({
              text: texto,
              muted: index > 0,
            })),
        },
        { label: "Cantidad", width: 55, value: (row) => formatCantidad(row.cantidad), align: "right" },
        {
          label: "C.Unit s/IVA",
          width: 58,
          value: (row) => formatCurrencyLabel(row.costUnitSinIva),
          align: "right",
        },
        {
          label: "C.Unit c/IVA",
          width: 58,
          value: (row) => formatCurrencyLabel(row.costUnitConIva),
          align: "right",
        },
        { label: "Valor s/IVA", width: 58, value: (row) => formatCurrencyLabel(row.valorSinIva), align: "right" },
        { label: "Valor c/IVA", width: 58, value: (row) => formatCurrencyLabel(row.valorConIva), align: "right" },
        { label: "Periodo costo", width: 110, value: (row) => row.periodoCosto || "-", multiline: true },
      ];

      const columnGap = 6;
      let currentX = marginX;
      const columnLayouts = columnsPdf.map((column) => {
        const layout = { ...column, x: currentX };
        currentX += column.width + columnGap;
        return layout;
      });

      const descripcionPlano = typeof config.description === "string" ? config.description : `${config.description}`;
      const descripcionTexto = ensureText(descripcionPlano);
      const tituloTexto = ensureText(config.title || "Detalle de movimientos");
      const usuarioTexto = ensureText(generadoPor);
      const timestampTexto = ensureText(timestampLabel);

      const drawPageHeader = () => {
        const rightX = pageWidth - marginX;
        let headerTop = marginY;
        if (logoDataUrl) {
          const logoWidth = 120;
          const logoHeight = 26;
          doc.addImage(logoDataUrl, "PNG", marginX, headerTop - 6, logoWidth, logoHeight);
          headerTop += logoHeight - 4;
        }
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.text(breadcrumbTexto, marginX, headerTop + 6);
        headerTop += 14;
        doc.setFont("helvetica", "bold");
        doc.setFontSize(15);
        doc.text(tituloTexto, marginX, headerTop + 6);
        headerTop += 20;
        doc.setFont("helvetica", "normal");
        doc.setFontSize(9);
        doc.text(descripcionTexto, marginX, headerTop + 4, { maxWidth: pageWidth - marginX * 2 - 150 });
        doc.setFont("helvetica", "bold");
        doc.text("Usuario:", rightX, marginY + 14, { align: "right" });
        doc.setFont("helvetica", "normal");
        doc.text(usuarioTexto || "—", rightX, marginY + 26, { align: "right" });
        doc.setFont("helvetica", "bold");
        doc.text("Generado:", rightX, marginY + 38, { align: "right" });
        doc.setFont("helvetica", "normal");
        doc.text(timestampTexto || "—", rightX, marginY + 50, { align: "right" });
        doc.setFont("helvetica", "bold");
        doc.text(`Página ${pageNumber}`, rightX, marginY + 62, { align: "right" });
        doc.setFontSize(tableFontSize);
        doc.setFont("helvetica", "normal");
      };

      const drawPageFooter = () => {
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        const footerText = `Generado por ${usuarioTexto || "—"} · ${timestampTexto || "—"} · Página ${pageNumber}`;
        doc.text(footerText, marginX, pageHeight - marginY / 2);
      };

      const renderHeaderRow = () => {
        doc.setFont("helvetica", "bold");
        columnLayouts.forEach((column) => {
          const textX = column.align === "right" ? (column.x ?? 0) + column.width : column.x ?? 0;
          const options = column.align === "right" ? { align: "right" as const } : undefined;
          doc.text(column.label, textX, currentY, options);
        });
        doc.setFont("helvetica", "normal");
        currentY += lineHeight;
      };

      const startNewPage = (initial: boolean) => {
        if (!initial) {
          drawPageFooter();
          doc.addPage();
          pageNumber += 1;
          pageWidth = doc.internal.pageSize.getWidth();
          pageHeight = doc.internal.pageSize.getHeight();
        }
        drawPageHeader();
        currentY = marginY + headerHeight;
        renderHeaderRow();
      };

      startNewPage(true);

      filas.forEach((row) => {
        const defaultColor = { r: 33, g: 37, b: 41 };
        const mutedColor = { r: 134, g: 142, b: 150 };
        const rowLines = columnLayouts.map((column) => {
          const baseEntries = column.getLineEntries
            ? column.getLineEntries(row)
            : [{ text: column.value(row) || "-" }];
          const availableWidth = Math.max(column.width - 6, 12);
          const lineEntries: PdfLineEntry[] = [];
          baseEntries.forEach((entry) => {
            const safeText = ensureText(entry.text);
            const wrap = column.multiline || safeText.length * 4 > availableWidth;
            const segments = wrap ? doc.splitTextToSize(safeText, availableWidth) : [safeText];
            segments.forEach((segment: string) => {
              lineEntries.push({ text: segment, muted: entry.muted });
            });
          });
          return { column, lines: lineEntries };
        });
        const maxLines = Math.max(...rowLines.map((item) => (item.lines.length > 0 ? item.lines.length : 1)));
        const rowHeight = maxLines * lineHeight;
        if (currentY + rowHeight > pageHeight - marginY) {
          startNewPage(false);
        }
        rowLines.forEach((item) => {
          const textX = item.column.align === "right" ? (item.column.x ?? 0) + item.column.width : item.column.x ?? 0;
          const options = item.column.align === "right" ? { align: "right" as const } : undefined;
          item.lines.forEach((line, index) => {
            if (line.muted) {
              doc.setTextColor(mutedColor.r, mutedColor.g, mutedColor.b);
            } else {
              doc.setTextColor(defaultColor.r, defaultColor.g, defaultColor.b);
            }
            doc.text(line.text || "-", textX, currentY + index * lineHeight, options);
          });
          doc.setTextColor(defaultColor.r, defaultColor.g, defaultColor.b);
        });
        currentY += rowHeight;
      });

      if (filas.length > 0) {
        if (currentY + lineHeight * 3 > pageHeight - marginY) {
          startNewPage(false);
        }
        doc.setFont("helvetica", "bold");
        doc.setFontSize(10);
        const totalsMap = new Map<string, PdfLineEntry[]>([
          ["Fecha / Tipo", [{ text: "Totales" }]],
          [origenDestinoLabel, [{ text: "" }]],
          ["Artículo", [{ text: "" }]],
          [
            "Cantidad",
            [{ text: `Cantidad: ${formatCantidad(config.totals.totalCantidad)}` }],
          ],
          [
            "C.Unit s/IVA",
            [{ text: formatCurrencyLabel(config.totals.promedioCostoSinIva) }],
          ],
          [
            "C.Unit c/IVA",
            [{ text: formatCurrencyLabel(config.totals.promedioCostoConIva) }],
          ],
          [
            "Valor s/IVA",
            [{ text: formatCurrencyLabel(config.totals.totalValorSinIva) }],
          ],
          [
            "Valor c/IVA",
            [{ text: formatCurrencyLabel(config.totals.totalValorConIva) }],
          ],
          ["Periodo costo", [{ text: "" }]],
        ]);
        let maxTotalLines = 1;
        columnLayouts.forEach((column) => {
          const entries = totalsMap.get(column.label);
          if (!entries || entries.length === 0) {
            return;
          }
          const textX = column.align === "right" ? (column.x ?? 0) + column.width : column.x ?? 0;
          const options = column.align === "right" ? { align: "right" as const } : undefined;
          let totalLinesForColumn = 0;
          entries.forEach((entry) => {
            const sanitized = entry.text || "";
            if (!sanitized) {
              return;
            }
            const availableWidth = Math.max(column.width - 6, 12);
            const wrapped = column.align === "right" ? [sanitized] : doc.splitTextToSize(sanitized, availableWidth);
            wrapped.forEach((linea: string, index: number) => {
              doc.text(linea, textX, currentY + lineHeight + totalLinesForColumn * lineHeight + index * lineHeight, options);
            });
            totalLinesForColumn += wrapped.length;
          });
          if (totalLinesForColumn > maxTotalLines) {
            maxTotalLines = totalLinesForColumn;
          }
        });
        doc.setFontSize(tableFontSize);
        doc.setFont("helvetica", "normal");
        currentY += lineHeight * (maxTotalLines + 1);
      }

      drawPageFooter();
      doc.save(config.fileName);
    },
    [breadcrumbTexto, logoDataUrl, mode],
  );

  const handleExportDetallePdf = useCallback(() => {
    if (!detalleContext || detalleRows.length === 0 || exportandoDetallePdf) {
      return;
    }
    const etiqueta = sanitizeFileName(detalleContext.label || detalleContext.view);
    const nombreArchivo = `detalle_${detalleContext.view}_${etiqueta || "movimientos"}.pdf`;
    try {
      setExportandoDetallePdf(true);
      generarPdfMovimientos(detalleRows, {
        title: detalleTitle || "Detalle de movimientos",
        description: detalleDescription,
        fileName: nombreArchivo,
        totals: detalleTotals,
      });
    } finally {
      setExportandoDetallePdf(false);
    }
  }, [
    detalleContext,
    detalleDescription,
    detalleRows,
    detalleTitle,
    detalleTotals,
    exportandoDetallePdf,
    generarPdfMovimientos,
  ]);

  const handleExportListadoPdf = useCallback(() => {
    if (normalizedRows.length === 0 || exportandoListadoPdf) {
      return;
    }
    const rango =
      fechaDesde && fechaHasta ? `${fechaDesde}_al_${fechaHasta}` : fechaDesde || fechaHasta || "sin_rango";
    const prefijo = exportPrefix;
    const nombreArchivo = `${prefijo}_listado_${sanitizeFileName(rango)}.pdf`;
    const descripcion = `${listadoDescripcionBase} entre ${fechaDesde || "?"} y ${fechaHasta || "?"} (${normalizedRows.length.toLocaleString(
      "es-PY",
    )} registros).`;
    try {
      setExportandoListadoPdf(true);
      generarPdfMovimientos(normalizedRows, {
        title: listadoTitulo,
        description: descripcion,
        fileName: nombreArchivo,
        totals: listadoTotals,
      });
    } finally {
      setExportandoListadoPdf(false);
    }
  }, [
    exportandoListadoPdf,
    fechaDesde,
    fechaHasta,
    generarPdfMovimientos,
    listadoTotals,
    normalizedRows,
  ]);

  const handleExportTablaActualExcel = useCallback(
    (view: AggregatedViewId) => {
      const rows = aggregatedData[view] ?? [];
      if (rows.length === 0 || exportandoTablaActualExcel) {
        return;
      }
      try {
        setExportandoTablaActualExcel(true);
        const label = aggregatedLabelByView[view];
        const dataset = rows.map((row) => ({
          [label]: row.etiqueta,
          Cantidad: Number(row.cantidad ?? 0),
          "Valor sin IVA": Number(row.valorSinIva ?? 0),
          "Valor con IVA": Number(row.valorConIva ?? 0),
        }));
        const workbook = XLSX.utils.book_new();
        const sheet = XLSX.utils.json_to_sheet(dataset);
        XLSX.utils.book_append_sheet(workbook, sheet, "Resumen");
        const rangeLabel =
          fechaDesde && fechaHasta ? `${fechaDesde}_al_${fechaHasta}` : fechaDesde || fechaHasta || "sin_rango";
        const slug = sanitizeFileName(rangeLabel) || "sin_rango";
        const prefijo = exportPrefix;
        const fileName = `${prefijo}_${view}_${slug}.xlsx`;
        XLSX.writeFile(workbook, fileName);
      } finally {
        setExportandoTablaActualExcel(false);
      }
    },
    [aggregatedData, aggregatedLabelByView, exportandoTablaActualExcel, exportPrefix, fechaDesde, fechaHasta],
  );

  const handleExportTablaActualPdf = useCallback(
    (view: AggregatedViewId) => {
      const rows = aggregatedData[view] ?? [];
      if (rows.length === 0 || exportandoTablaActualPdf) {
        return;
      }
      try {
        setExportandoTablaActualPdf(true);
        const doc = new jsPDF({ orientation: "landscape", unit: "pt", format: "a4" });
        let pageWidth = doc.internal.pageSize.getWidth();
        let pageHeight = doc.internal.pageSize.getHeight();
        const marginX = 28;
        const marginY = 28;
        const lineHeight = 14;
        const tableFontSize = 9;
        const headerHeight = 105;
        const generadoPor = resolveUsuarioActual();
        const timestamp = new Date();
        const timestampLabel = timestamp.toLocaleString("es-PY", {
          dateStyle: "short",
          timeStyle: "medium",
        });
        const columnLabel = aggregatedLabelByView[view];
        const rangeText =
          fechaDesde && fechaHasta ? `${fechaDesde} al ${fechaHasta}` : fechaDesde || fechaHasta || "Sin rango definido";
        const findTabLabel = tableViews.find((tab) => tab.id === view)?.label ?? columnLabel;
        const columns: {
          label: string;
          width: number;
          key: keyof MovimientosAgrupadosApiRow;
          align?: "right";
        }[] = [
          { key: "etiqueta", label: columnLabel, width: 220 },
          { key: "cantidad", label: "Cantidad", width: 90, align: "right" },
          { key: "valorSinIva", label: "Valor s/IVA", width: 120, align: "right" },
          { key: "valorConIva", label: "Valor c/IVA", width: 120, align: "right" },
        ];
        const columnGap = 12;
        let currentX = marginX;
        const columnLayouts = columns.map((column) => {
          const layout = { ...column, x: currentX };
          currentX += column.width + columnGap;
          return layout;
        });
        const descripcionTexto = `Resumen de ${findTabLabel} · ${rows.length.toLocaleString("es-PY")} registros · ${rangeText}`;
        const usuarioTexto = ensureText(generadoPor);
        const timestampTexto = ensureText(timestampLabel);
        let currentY = marginY;
        let pageNumber = 1;

        const drawPageHeader = () => {
          const rightX = pageWidth - marginX;
          let headerTop = marginY;
          if (logoDataUrl) {
            const logoWidth = 120;
            const logoHeight = 26;
            doc.addImage(logoDataUrl, "PNG", marginX, headerTop - 6, logoWidth, logoHeight);
            headerTop += logoHeight - 4;
          }
          doc.setFont("helvetica", "normal");
          doc.setFontSize(8);
          doc.text(breadcrumbTexto, marginX, headerTop + 6);
          headerTop += 14;
          doc.setFont("helvetica", "bold");
          doc.setFontSize(15);
          doc.text(`${exportSheetName} · ${findTabLabel}`, marginX, headerTop + 6);
          headerTop += 20;
          doc.setFont("helvetica", "normal");
          doc.setFontSize(9);
          doc.text(descripcionTexto, marginX, headerTop + 4, { maxWidth: pageWidth - marginX * 2 - 150 });
          doc.setFont("helvetica", "bold");
          doc.text("Usuario:", rightX, marginY + 14, { align: "right" });
          doc.setFont("helvetica", "normal");
          doc.text(usuarioTexto || "—", rightX, marginY + 26, { align: "right" });
          doc.setFont("helvetica", "bold");
          doc.text("Generado:", rightX, marginY + 38, { align: "right" });
          doc.setFont("helvetica", "normal");
          doc.text(timestampTexto || "—", rightX, marginY + 50, { align: "right" });
          doc.setFont("helvetica", "bold");
          doc.text(`Página ${pageNumber}`, rightX, marginY + 62, { align: "right" });
          doc.setFontSize(tableFontSize);
          doc.setFont("helvetica", "normal");
        };

        const drawPageFooter = () => {
          doc.setFont("helvetica", "normal");
          doc.setFontSize(8);
          const footerText = `Generado por ${usuarioTexto || "—"} · ${timestampTexto || "—"} · Página ${pageNumber}`;
          doc.text(footerText, marginX, pageHeight - marginY / 2);
        };

        const renderHeaderRow = () => {
          doc.setFont("helvetica", "bold");
          columnLayouts.forEach((column) => {
            const textX = column.align === "right" ? (column.x ?? 0) + column.width : column.x ?? 0;
            const options = column.align === "right" ? { align: "right" as const } : undefined;
            doc.text(column.label, textX, currentY, options);
          });
          doc.setFont("helvetica", "normal");
          currentY += lineHeight;
        };

        const startNewPage = (initial: boolean) => {
          if (!initial) {
            drawPageFooter();
            doc.addPage();
            pageNumber += 1;
            pageWidth = doc.internal.pageSize.getWidth();
            pageHeight = doc.internal.pageSize.getHeight();
          }
          drawPageHeader();
          currentY = marginY + headerHeight;
          renderHeaderRow();
        };

        startNewPage(true);

        const drawRowValue = (columnKey: keyof MovimientosAgrupadosApiRow, row: MovimientosAgrupadosApiRow) => {
          switch (columnKey) {
            case "cantidad":
              return formatCantidad(row.cantidad, 2);
            case "valorSinIva":
              return formatCurrencyLabel(row.valorSinIva);
            case "valorConIva":
              return formatCurrencyLabel(row.valorConIva);
            default:
              return ensureText(row.etiqueta);
          }
        };

        rows.forEach((row) => {
          if (currentY + lineHeight > pageHeight - marginY) {
            startNewPage(false);
          }
          columnLayouts.forEach((column) => {
            const textX = column.align === "right" ? (column.x ?? 0) + column.width : column.x ?? 0;
            const options = column.align === "right" ? { align: "right" as const } : undefined;
            const text = drawRowValue(column.key, row);
            doc.text(text, textX, currentY, options);
          });
          currentY += lineHeight;
        });

        const totalCantidad = rows.reduce((acc, row) => acc + Number(row.cantidad ?? 0), 0);
        const totalSinIva = rows.reduce((acc, row) => acc + Number(row.valorSinIva ?? 0), 0);
        const totalConIva = rows.reduce((acc, row) => acc + Number(row.valorConIva ?? 0), 0);

        if (rows.length > 0) {
          if (currentY + lineHeight * 3 > pageHeight - marginY) {
            startNewPage(false);
          }
          doc.setFont("helvetica", "bold");
          doc.text(`Total registros: ${rows.length.toLocaleString("es-PY")}`, marginX, currentY + lineHeight);
          doc.text(`Total cant.: ${formatCantidad(totalCantidad, 2)}`, marginX, currentY + lineHeight * 2);
          doc.text(`Total s/IVA: ${formatCurrencyLabel(totalSinIva)}`, marginX + 220, currentY + lineHeight);
          doc.text(`Total c/IVA: ${formatCurrencyLabel(totalConIva)}`, marginX + 220, currentY + lineHeight * 2);
        }

        drawPageFooter();
        const rangeSlug = sanitizeFileName(rangeText) || "sin_rango";
        doc.save(`${exportPrefix}_${view}_${rangeSlug}.pdf`);
      } finally {
        setExportandoTablaActualPdf(false);
      }
    },
    [
      aggregatedData,
      aggregatedLabelByView,
      breadcrumbTexto,
      exportandoTablaActualPdf,
      exportPrefix,
      exportSheetName,
      fechaDesde,
      fechaHasta,
      logoDataUrl,
      tableViews,
    ],
  );

  const handleClearFilters = useCallback(() => {
    setSelectedTipos([]);
    setSelectedIdMovs([]);
    setSelectedFamilias([]);
    setSelectedLineas([]);
    setSelectedRecibes([]);
    setSelectedDescripciones([]);
    setActiveCategory("all");
    setActiveTableTab("detalle");
  }, []);

  const renderTableActions = useCallback(
    (tabId: string) => {
      const filterButton = (
        <Button color="secondary" onClick={() => setFiltrosAbiertos(true)}>
          <i className="ri-equalizer-line me-1" /> Filtros
        </Button>
      );

      if (tabId === "detalle") {
        const exportDisabled = !hasFetched || filteredMovimientos.length === 0 || exportando;

        return (
          <>
            <Button color="success" onClick={handleExportarExcel} disabled={exportDisabled}>
              {exportando ? (
                <>
                  <Spinner size="sm" className="me-1" /> Exportando
                </>
              ) : (
                <>
                  <i className="ri-download-2-line me-1" /> Exportar Excel
                </>
              )}
            </Button>
            <Button color="danger" onClick={handleExportListadoPdf} disabled={exportDisabled || exportandoListadoPdf}>
              {exportandoListadoPdf ? (
                <>
                  <Spinner size="sm" className="me-1" /> Generando
                </>
              ) : (
                <>
                  <i className="ri-file-pdf-line me-1" /> Exportar PDF
                </>
              )}
            </Button>
            {filterButton}
          </>
        );
      }

      if (tabId === "depositos" || tabId === "familias" || tabId === "articulos") {
        const rows = aggregatedData[tabId as AggregatedViewId] ?? [];
        const disabled = rows.length === 0;
        return (
          <>
            <Button
              color="success"
              onClick={() => handleExportTablaActualExcel(tabId as AggregatedViewId)}
              disabled={disabled || exportandoTablaActualExcel}
            >
              {exportandoTablaActualExcel ? (
                <>
                  <Spinner size="sm" className="me-1" /> Exportando
                </>
              ) : (
                <>
                  <i className="ri-download-2-line me-1" /> Exportar Excel
                </>
              )}
            </Button>
            <Button
              color="danger"
              onClick={() => handleExportTablaActualPdf(tabId as AggregatedViewId)}
              disabled={disabled || exportandoTablaActualPdf}
            >
              {exportandoTablaActualPdf ? (
                <>
                  <Spinner size="sm" className="me-1" /> Generando
                </>
              ) : (
                <>
                  <i className="ri-file-pdf-line me-1" /> Exportar PDF
                </>
              )}
            </Button>
            {filterButton}
          </>
        );
      }

      return filterButton;
    },
    [
      aggregatedData,
      detalleContext,
      exportando,
      exportandoListadoPdf,
      exportandoTablaActualExcel,
      exportandoTablaActualPdf,
      filteredMovimientos.length,
      handleExportListadoPdf,
      handleExportTablaActualExcel,
      handleExportTablaActualPdf,
      handleExportarExcel,
      hasFetched,
    ],
  );

  return (
    <>
      <Row className="g-3">
        <StockTransferenciasDetailAnalytics
          kpiTabs={kpiTabs}
          activeKpiTab={kpiTab}
          onKpiTabChange={setKpiTab}
          chartTabs={chartTabsData}
          activeChartTab={chartsTab}
          onChartTabChange={setChartsTab}
          chartsLoading={initialLoading}
          kpisLoading={initialLoading}
        />
        <StockTransferenciasDetailTable
          tabs={tableViews}
          activeTab={activeTableTab}
          onTabChange={setActiveTableTab}
          columns={currentDataTableColumns}
          data={tableData}
          emptyMessage={emptyMessage}
          renderActions={renderTableActions}
          isLoading={initialLoading}
      />
      </Row>
      <StockTransferenciasFiltersDrawer
        isOpen={filtrosAbiertos}
        onClose={() => setFiltrosAbiertos(false)}
        selectedTipos={selectedTipos}
        onSelectedTiposChange={handleTiposChange}
        options={tipoOptions}
        categories={categoryOptions}
        activeCategory={activeCategory}
        onCategoryChange={(valor) => {
          setActiveCategory(valor);
          setActiveTableTab("detalle");
        }}
        onClear={handleClearFilters}
        disabled={filtersDisabled}
        fechaDesde={fechaDesde}
        fechaHasta={fechaHasta}
        onFechaDesdeChange={setFechaDesde}
        onFechaHastaChange={setFechaHasta}
        onConsultar={fetchMovimientos}
        disableConsultar={disableConsultar}
        loadingMovimientos={loadingMovimientos}
        errorMovimientos={errorMovimientos}
        idMovOptions={idMovOptions}
        selectedIdMovs={selectedIdMovs}
        onIdMovChange={handleIdMovChange}
        familiaOptions={familiaOptions}
        selectedFamilias={selectedFamilias}
        onFamiliaChange={handleFamiliasChange}
        lineaOptions={lineaOptions}
        selectedLineas={selectedLineas}
        onLineaChange={handleLineasChange}
        serverOptions={serverOptions}
        selectedServers={selectedServers}
        onServerChange={handleServersChange}
        recibeOptions={recibeOptions}
        selectedRecibes={selectedRecibes}
        onRecibeChange={handleRecibesChange}
        descripcionOptions={descripcionOptions}
        selectedDescripciones={selectedDescripciones}
        onDescripcionChange={handleDescripcionesChange}
      />
      <StockTransferenciasDetailOffcanvas
        isOpen={Boolean(detalleContext)}
        onClose={handleCloseDetalle}
        title={detalleTitle}
        description={detalleDescription}
        columns={detailColumns}
        data={detalleRows}
        onExportExcel={handleExportDetalleExcel}
        onExportPdf={handleExportDetallePdf}
        disableActions={!detalleContext || detalleRows.length === 0}
        exportingExcel={exportandoDetalleExcel}
        exportingPdf={exportandoDetallePdf}
        totals={detalleTotals}
      />
    </>
  );
};

export default StockTransferenciasDetailTab;
